import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatScheduledDate } from '../utils/dateUtils';
import { 
  Bot, 
  Calendar, 
  Clock, 
  Edit2, 
  Trash2, 
  Play, 
  Pause, 
  AlertCircle,
  CheckCircle,
  Image as ImageIcon,
  Plus,
  BarChart3,
  Settings,
  Zap,
  FileText,
  Eye,
  RefreshCw,
  Share2,
  X,
  XCircle,
  Users,
  Mail,
  MessageCircle,
  MessageSquare
} from 'lucide-react';

const AIAgentsDashboard = () => {
  const navigate = useNavigate();
  const { user, token, isAdmin, loading: authLoading } = useAuth();
  const [agents, setAgents] = useState([]);
  const [allAgents, setAllAgents] = useState([]); // Store all agents for stats
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [pageLoading, setPageLoading] = useState(true);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [processingAgent, setProcessingAgent] = useState(null);
  // Initialize filters from localStorage or defaults
  const [statusFilter, setStatusFilter] = useState(() => {
    return localStorage.getItem('aiAgentsDashboard_statusFilter') || 'all';
  });
  const [agentTypeFilter, setAgentTypeFilter] = useState(() => {
    return localStorage.getItem('aiAgentsDashboard_agentTypeFilter') || 'all';
  });
  const [showAgentTypeModal, setShowAgentTypeModal] = useState(false);
  const [agentTypes, setAgentTypes] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [payPeriodSettings, setPayPeriodSettings] = useState([]);
  const [originalStartDate, setOriginalStartDate] = useState(null);
  const [businessTimezone, setBusinessTimezone] = useState(null);
  const [businessTime, setBusinessTime] = useState(null);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [runningAgent, setRunningAgent] = useState(null);
  const [showRunModal, setShowRunModal] = useState(false);
  const [runStatus, setRunStatus] = useState('');
  const [runMessage, setRunMessage] = useState('');
  const [showDuplicateTimesheetModal, setShowDuplicateTimesheetModal] = useState(false);
  const [duplicateTimesheetInfo, setDuplicateTimesheetInfo] = useState(null);
  const [pendingAgentRun, setPendingAgentRun] = useState(null);
  const [showAIErrorModal, setShowAIErrorModal] = useState(false); // AI Connection Error Modal
  const [holidays, setHolidays] = useState([]);
  const [availableHolidays, setAvailableHolidays] = useState([]);
  const [customerData, setCustomerData] = useState([]);

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || (window.location.origin.includes("localhost") ? "http://localhost:8000" : window.location.origin + "/pandvah-backend");

  // Auth check - simplified approach
  useEffect(() => {
    if (!authLoading && user && token) {
      console.log('Dashboard loading with token, fetching data...');
      setPageLoading(false);
      fetchAgents();
      fetchHolidays();
      fetchAvailableHolidays();
      fetchCustomerData();
      fetchEmployees();
      fetchPayPeriodSettings();
      fetchBusinessInfo(); // Fetch business timezone
      fetchTimesheetConfig().then(startDate => setOriginalStartDate(startDate));
    } else if (!authLoading) {
      setPageLoading(false);
    }
  }, [user, authLoading, token]);

  // Save filters to localStorage when they change
  useEffect(() => {
    localStorage.setItem('aiAgentsDashboard_statusFilter', statusFilter);
  }, [statusFilter]);

  useEffect(() => {
    localStorage.setItem('aiAgentsDashboard_agentTypeFilter', agentTypeFilter);
  }, [agentTypeFilter]);

  // Refresh customer data when viewing an SMS or Email agent
  useEffect(() => {
    if (selectedAgent && (selectedAgent.agent_type === 'sms_agent' || selectedAgent.agent_type === 'email') && (!customerData || !customerData.customers)) {
      console.log('Refreshing customer data for SMS/Email agent view...');
      fetchCustomerData();
    }
  }, [selectedAgent]);

  // Fetch holidays
  const fetchHolidays = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/holidays`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setHolidays(data);
      } else {
        console.error('Failed to fetch holidays');
      }
    } catch (error) {
      console.error('Error fetching holidays:', error);
    }
  };

  // Fetch available holidays
  const fetchAvailableHolidays = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/holidays`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Available holidays fetched:', data); // Debug log
        setAvailableHolidays(data);
      } else {
        console.error('Failed to fetch available holidays');
      }
    } catch (error) {
      console.error('Error fetching available holidays:', error);
    }
  };

  // Fetch customer data
  const fetchCustomerData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/customers?limit=1000`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Customer data fetched:', data.customers?.length || 0, 'customers'); // Simple log
        setCustomerData(data);
      } else {
        console.error('Failed to fetch customer data, status:', response.status);
      }
    } catch (error) {
      console.error('Error fetching customer data:', error);
    }
  };

  const fetchAgents = async (forceRefresh = false) => {
    setLoading(true);
    try {
      // Add cache-busting parameter when force refreshing
      const url = forceRefresh 
        ? `${API_BASE_URL}/api/ai-agents?_t=${Date.now()}`
        : `${API_BASE_URL}/api/ai-agents`;
        
      // Fetch all agents from unified ai_agents endpoint (includes all agent types)
      const response = await fetch(url, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          // Add cache control headers to ensure fresh data
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      if (response.ok) {
        const allAgents = await response.json();
        
        // Enrich paystub agents with timesheet data
        const enrichedAgents = await enrichAgentsWithTimesheetData(allAgents);
        setAllAgents(enrichedAgents);
        
        // Filter agents based on current filters
        let filteredAgents = enrichedAgents;
        
        // Apply status filter
        if (statusFilter === 'active') {
          filteredAgents = filteredAgents.filter(agent => agent.is_active);
        } else if (statusFilter === 'paused') {
          filteredAgents = filteredAgents.filter(agent => !agent.is_active);
        }
        
        // Apply agent type filter
        if (agentTypeFilter !== 'all') {
          filteredAgents = filteredAgents.filter(agent => agent.agent_type === agentTypeFilter);
        }
        
        setAgents(filteredAgents);
      } else {
        setMessage({ type: 'error', text: 'Failed to fetch agents' });
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  // Format time from 24-hour to 12-hour AM/PM format
  const formatTime = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':');
    const hour24 = parseInt(hours);
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const ampm = hour24 >= 12 ? 'PM' : 'AM';
    return `${hour12}:${minutes} ${ampm}`;
  };

  const applyFilters = (newStatusFilter = statusFilter, newAgentTypeFilter = agentTypeFilter) => {
    let filteredAgents = allAgents;
    
    // Apply status filter
    if (newStatusFilter === 'active') {
      filteredAgents = filteredAgents.filter(agent => agent.is_active);
    } else if (newStatusFilter === 'paused') {
      filteredAgents = filteredAgents.filter(agent => !agent.is_active);
    }
    
    // Apply agent type filter
    if (newAgentTypeFilter !== 'all') {
      filteredAgents = filteredAgents.filter(agent => agent.agent_type === newAgentTypeFilter);
    }
    
    setAgents(filteredAgents);
  };

  const handleStatusFilterChange = (newFilter) => {
    setStatusFilter(newFilter);
    applyFilters(newFilter, agentTypeFilter);
  };

  const handleAgentTypeFilterChange = (newFilter) => {
    setAgentTypeFilter(newFilter);
    applyFilters(statusFilter, newFilter);
  };

  const fetchAgentTypes = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai-agent-types`);
      if (response.ok) {
        const types = await response.json();
        setAgentTypes(types);
      }
    } catch (error) {
      console.error('Error fetching agent types:', error);
    }
  };

  const handleNewAgentClick = () => {
    setShowAgentTypeModal(true);
    fetchAgentTypes();
  };

  const handleAgentTypeSelect = (agentType) => {
    setShowAgentTypeModal(false);
    // Navigate to config with agent type
    navigate(`/ai-agent-config?agent_type=${agentType.value}`);
  };

  const handleEditAgent = (agent) => {
    try {
      console.log('Editing agent data:', agent);
      
      // Store the agent data in localStorage for the edit page to use
      localStorage.setItem('editingAgent', JSON.stringify(agent));
      
      // Verify storage
      const stored = localStorage.getItem('editingAgent');
      console.log('Stored in localStorage:', stored);
      
      // Navigate to the AI agent configuration page with agent type parameter
      const agentType = agent.agent_type || 'social_media'; // Default to social_media if not specified
      navigate(`/ai-agent-config?mode=edit&id=${agent.id}&agent_type=${agentType}`);
    } catch (error) {
      console.error('Error navigating to edit agent:', error);
      setMessage({ type: 'error', text: 'Failed to open edit page. Please try again.' });
    }
  };

  const handleRunAgent = (agent) => {
    try {
      console.log('Running agent data:', agent);
      
      // Check for existing timesheets if it's a timesheet agent
      if (agent.agent_type === 'time_sheet') {
        checkForExistingTimesheet(agent);
      } else {
        // All other agents use the unified modal approach
        runAgentWithModal(agent);
      }
    } catch (error) {
      console.error('Error running agent:', error);
      setMessage({ type: 'error', text: 'Failed to run agent. Please try again.' });
    }
  };

  // Check for existing timesheet for the same period
  const checkForExistingTimesheet = async (agent) => {
    try {
      console.log('=== DUPLICATE TIMESHEET CHECK ===');
      console.log('Checking for existing timesheets for agent:', agent.id);
      
      // Calculate the period dates based on agent settings
      const periodInfo = calculatePayPeriodAndRunDate(agent);
      console.log('Period info calculated:', periodInfo);
      
      if (!periodInfo || !periodInfo.payPeriodRange || periodInfo.payPeriodRange.includes('Error') || periodInfo.payPeriodRange.includes('Set foundation date')) {
        console.log('Cannot calculate period, proceeding with agent run');
        // If we can't calculate the period, proceed with running the agent
        runAgentWithModal(agent);
        return;
      }

      // Search for existing timesheet posts from all workflow states
      console.log('Fetching existing posts from all workflow states...');
      
      const [inReviewResponse, readyToPublishResponse, publishedResponse] = await Promise.all([
        fetch(`${API_BASE_URL}/api/ai-posts/in-review?page=1&limit=50`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/api/ai-posts/ready-to-publish?page=1&limit=50`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_BASE_URL}/api/ai-posts/published?page=1&limit=50`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      let allPosts = [];
      
      if (inReviewResponse.ok) {
        const inReviewData = await inReviewResponse.json();
        const inReviewPosts = inReviewData.posts || [];
        console.log('In Review posts:', inReviewPosts.length);
        allPosts = allPosts.concat(inReviewPosts);
      }
      
      if (readyToPublishResponse.ok) {
        const readyData = await readyToPublishResponse.json();
        const readyPosts = readyData.posts || [];
        console.log('Ready to Publish posts:', readyPosts.length);
        allPosts = allPosts.concat(readyPosts);
      }
      
      if (publishedResponse.ok) {
        const publishedData = await publishedResponse.json();
        const publishedPosts = publishedData.posts || [];
        console.log('Published posts:', publishedPosts.length);
        allPosts = allPosts.concat(publishedPosts);
      }
      
      console.log('Total posts from all endpoints:', allPosts.length);
      console.log('Total posts from all endpoints:', allPosts.length);
      
      // Filter for timesheet posts from this agent
      const timesheetPosts = allPosts.filter(post => {
        const isTimesheet = post.agent_type === 'time_sheet';
        const isFromThisAgent = post.agent_id === agent.id;
        const isInWorkflow = post.status === 'in_review' || post.status === 'ready_to_publish' || post.status === 'published';
        
        console.log(`Post ${post.id}: agent_type=${post.agent_type}, agent_id=${post.agent_id}, status=${post.status}`);
        console.log(`  - isTimesheet: ${isTimesheet}, isFromThisAgent: ${isFromThisAgent}, isInWorkflow: ${isInWorkflow}`);
        
        return isTimesheet && isFromThisAgent && isInWorkflow;
      });
      
      console.log('Filtered timesheet posts:', timesheetPosts.length, timesheetPosts);

      if (timesheetPosts.length > 0) {
        // Find the most recent one
        const existingTimesheet = timesheetPosts[0];
        console.log('Found existing timesheet:', existingTimesheet);
        
        setDuplicateTimesheetInfo({
          existingTimesheet,
          period: periodInfo.payPeriodRange,
          status: existingTimesheet.status
        });
        setPendingAgentRun(agent);
        setShowDuplicateTimesheetModal(true);
        console.log('Showing duplicate timesheet modal');
      } else {
        console.log('No duplicate timesheets found, proceeding with agent run');
        // No duplicates found, proceed with running
        runAgentWithModal(agent);
      }
    } catch (error) {
      console.error('Error checking for existing timesheet:', error);
      // If check fails, proceed with running (better than blocking)
      runAgentWithModal(agent);
    }
    
    console.log('=================================');
  };

  // Handle user's choice about duplicate timesheet
  const handleDuplicateTimesheetChoice = async (continueWithRun) => {
    setShowDuplicateTimesheetModal(false);
    
    if (continueWithRun && pendingAgentRun && duplicateTimesheetInfo) {
      try {
        // Delete the existing timesheet post first
        const existingPostId = duplicateTimesheetInfo.existingTimesheet.id;
        console.log(`Deleting existing timesheet post: ${existingPostId}`);
        
        const deleteResponse = await fetch(`${API_BASE_URL}/api/ai-posts/${existingPostId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (deleteResponse.ok) {
          console.log('Existing timesheet deleted successfully');
          // Now run the agent to create new timesheet
          runAgentWithModal(pendingAgentRun);
        } else {
          console.error('Failed to delete existing timesheet');
          setMessage({ type: 'error', text: 'Failed to delete existing timesheet. Please try again.' });
        }
      } catch (error) {
        console.error('Error deleting existing timesheet:', error);
        setMessage({ type: 'error', text: 'Error deleting existing timesheet. Please try again.' });
      }
    }
    
    // Reset states
    setDuplicateTimesheetInfo(null);
    setPendingAgentRun(null);
  };

  const runAgentWithModal = async (agent) => {
    setRunningAgent(agent);
    setShowRunModal(true);
    setRunStatus('running');
    setRunMessage('Agent is running...');

    try {
      // For timesheet agents, calculate the current pay period to send to backend
      let requestBody = {};
      
      if (agent.agent_type === 'time_sheet') {
        const periodInfo = calculatePayPeriodAndRunDate(agent);
        if (periodInfo && periodInfo.currentPeriodStart && periodInfo.currentPeriodEnd) {
          // Use the actual calculated start and end dates
          const startDate = new Date(periodInfo.currentPeriodStart);
          const endDate = new Date(periodInfo.currentPeriodEnd);
          
          // Format as YYYY-MM-DD in business timezone, avoiding timezone shifts
          const formatDateForBackend = (date) => {
            // Use UTC methods to avoid timezone conversion issues
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const formatted = `${year}-${month}-${day}`;
            console.log(`Formatting date ${date} as ${formatted}`);
            return formatted;
          };
          
          requestBody = {
            custom_period: {
              start_date: formatDateForBackend(startDate),
              end_date: formatDateForBackend(endDate)
            }
          };
          console.log('=== FRONTEND PAY PERIOD DEBUG ===');
          console.log('Dashboard calculated start date:', startDate);
          console.log('Dashboard calculated end date:', endDate);
          console.log('Formatted start_date for backend:', formatDateForBackend(startDate));
          console.log('Formatted end_date for backend:', formatDateForBackend(endDate));
          console.log('Sending timesheet period to backend:', requestBody);
          console.log('===================================');
        }
      }
      
      // Call unified backend API to run any agent type
      // Create AbortController for timeout (5 minutes for social media agents)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 300000); // 5 minutes
      
      const response = await fetch(`${API_BASE_URL}/api/ai-agents/${agent.id}/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: Object.keys(requestBody).length > 0 ? JSON.stringify(requestBody) : undefined,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (response.ok) {
        const result = await response.json();
        setRunStatus('completed');
        
        // Customize success message based on agent mode
        let successMessage = 'Content generated successfully!';
        if (agent.mode === 'auto' || agent.mode === 'recurring') {
          successMessage = 'Recurring agent executed successfully! Content generated based on your schedule.';
        } else if (agent.mode === 'adhoc') {
          successMessage = 'Adhoc agent executed successfully! Content generated and ready for review.';
        } else if (agent.mode === 'write') {
          successMessage = 'Write Your Post agent executed successfully! Your content has been generated.';
        }
        
        setRunMessage(successMessage + ' Check the In Review or Ready to Publish pages.');
        
        setTimeout(() => {
          setShowRunModal(false);
          setRunningAgent(null);
          setRunStatus('');
          setRunMessage('');
        }, 3000);
      } else {
        const errorData = await response.json();
        
        // Check if error is related to AI service failure
        const errorMessage = errorData.detail || 'Failed to generate content';
        const isAIError = errorMessage.toLowerCase().includes('openai') || 
                         errorMessage.toLowerCase().includes('openrouter') ||
                         errorMessage.toLowerCase().includes('chatgpt') ||
                         errorMessage.toLowerCase().includes('api call failed') ||
                         errorMessage.toLowerCase().includes('empty content');
        
        if (isAIError) {
          // Show AI connection error modal
          setShowRunModal(false);
          setRunningAgent(null);
          setShowAIErrorModal(true);
        } else {
          // Show regular error in run modal
          setRunStatus('error');
          setRunMessage(errorMessage);
        }
      }
    } catch (error) {
      console.error('Error running agent:', error);
      
      // Check if it's a timeout error (AbortError)
      if (error.name === 'AbortError') {
        setRunStatus('warning');
        setRunMessage(
          `Request timed out after 5 minutes. The agent may still be generating content in the background. ` +
          `Please check the "In Review" page in a few moments to see if content was generated. ` +
          `If you're generating content for multiple platforms, this is normal.`
        );
        // Don't auto-close on timeout so user can read the message
        return;
      }
      
      // Check if it's a network or AI service error
      const errorMessage = error.message || 'Network error';
      const isAIError = errorMessage.toLowerCase().includes('openai') || 
                       errorMessage.toLowerCase().includes('openrouter') ||
                       errorMessage.toLowerCase().includes('chatgpt') ||
                       errorMessage.toLowerCase().includes('api');
      
      if (isAIError) {
        // Show AI connection error modal
        setShowRunModal(false);
        setRunningAgent(null);
        setShowAIErrorModal(true);
      } else {
        // Show regular error in run modal
        setRunStatus('error');
        setRunMessage('Network error. Please check your connection.');
      }
    }
  };

  const openDeleteModal = (agent) => {
    setAgentToDelete(agent);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setAgentToDelete(null);
  };

  const confirmDeleteAgent = async () => {
    if (!agentToDelete) return;

    setProcessingAgent(agentToDelete.id);
    setShowDeleteModal(false);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai-agents/${agentToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'AI agent deleted successfully!' });
        fetchAgents(true); // Force refresh after deletion
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to delete agent' });
      }
    } catch (error) {
      console.error('Error deleting agent:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setProcessingAgent(null);
      setAgentToDelete(null);
    }
  };

  const toggleAgentStatus = async (agentId, currentStatus) => {
    setProcessingAgent(agentId);
    try {
      const response = await fetch(`${API_BASE_URL}/api/ai-agents/${agentId}/toggle`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setMessage({ 
          type: 'success', 
          text: `AI agent ${currentStatus ? 'paused' : 'activated'} successfully!` 
        });
        fetchAgents(true); // Force refresh after status toggle
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.detail || 'Failed to update agent status' });
      }
    } catch (error) {
      console.error('Error updating agent:', error);
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setProcessingAgent(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // getModeIcon function removed - icons no longer displayed in front of agent names

  const getModeColor = (mode) => {
    const colors = {
      auto: 'var(--theme-primary)',
      adhoc: '#f59e0b',
      write: '#8b5cf6'
    };
    return colors[mode] || 'var(--theme-primary)';
  };

  // Helper function to get holiday details for marketing agents
  const getHolidayDetails = (holidayIds) => {
    if (!holidayIds || holidayIds.length === 0) return 'No holidays selected';
    
    // If we have holiday data, format it nicely
    const holidayNames = holidayIds.map(holidayId => {
      // Try to find holiday name if we have the data
      // For now, just return the ID or try to format it
      return holidayId;
    });
    
    if (holidayNames.length === 1) {
      return holidayNames[0];
    } else if (holidayNames.length <= 3) {
      return holidayNames.join(', ');
    } else {
      return `${holidayNames.slice(0, 2).join(', ')} and ${holidayNames.length - 2} more`;
    }
  };

  // Helper function to format marketing agent content type display
  const getMarketingContentTypeDisplay = (agent) => {
    const contentType = agent.marketing_content_type;
    
    if (contentType === 'topic') {
      const topicName = agent.topic || agent.marketing_selected_topic || 'Unnamed Topic';
      return `Topic - ${topicName}`;
    } else if (contentType === 'holidays') {
      return 'Holiday Message Campaign';
    } else if (contentType === 'custom_campaign') {
      return 'Custom Campaign';
    } else {
      return 'Topic'; // Default fallback
    }
  };

  const getMarketingChatGPTStatus = (agent) => {
    const contentType = agent.marketing_content_type;
    
    // For topic and holidays, always show ticks for all channels
    if (contentType === 'topic' || contentType === 'holidays') {
      return {
        socialMedia: true,
        email: true,
        sms: true
      };
    } 
    
    // For custom campaigns, check individual ChatGPT settings
    if (contentType === 'custom_campaign') {
      return {
        socialMedia: agent.use_chatgpt_formatting || false, // Main campaign ChatGPT maps to social media
        email: agent.marketing_email_use_chatgpt || false,
        sms: agent.marketing_sms_use_chatgpt || false
      };
    }
    
    return { socialMedia: false, email: false, sms: false };
  };

  const getModeLabel = (mode, agentType, selectedHolidays) => {
    // Special case for Marketing Agent - always show Campaign Mode
    if (agentType === 'marketing_agent') {
      return 'Campaign';
    }
    
    // Special case for SMS and Email agents with holidays (scheduled mode)
    if ((agentType === 'sms_agent' || agentType === 'email') && mode === 'recurring' && selectedHolidays && selectedHolidays.length > 0) {
      return 'Scheduled Mode';
    }
    
    const labels = {
      auto: 'Recurring Mode',  // Legacy support
      recurring: 'Recurring Mode',
      adhoc: 'Adhoc Mode',
      write: 'Custom Post Mode'
    };
    return labels[mode] || mode;
  };

  // Fetch timesheet data for paystub agents
  const enrichAgentsWithTimesheetData = async (agents) => {
    const enrichedAgents = await Promise.all(
      agents.map(async (agent) => {
        // Only enrich paystub agents that have selected timesheet
        if (agent.agent_type === 'paystub_agent' && agent.selected_timesheet_id) {
          try {
            // Fetch the specific timesheet data from published posts
            const response = await fetch(`${API_BASE_URL}/api/ai-posts/published?limit=100`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
              const data = await response.json();
              const publishedPosts = data.posts || data;
              
              // Find the specific timesheet post
              const timesheetPost = publishedPosts.find(post => 
                post.id === agent.selected_timesheet_id && 
                (post.platforms?.includes('timesheet') || post.agent_type === 'time_sheet')
              );
              
              if (timesheetPost) {
                // Enrich agent with timesheet data
                return {
                  ...agent,
                  timesheet_data: {
                    agent_name: timesheetPost.agent_name,
                    start_date: timesheetPost.timesheet_data?.start_date,
                    end_date: timesheetPost.timesheet_data?.end_date,
                    published_at: timesheetPost.published_at,
                    created_at: timesheetPost.created_at,
                    type: timesheetPost.agent_name?.toLowerCase().includes('recurring') ? 'recurring' : 'adhoc'
                  }
                };
              }
            }
          } catch (error) {
            console.error('Error fetching timesheet data for agent:', agent.id, error);
          }
        }
        return agent;
      })
    );
    
    return enrichedAgents;
  };

  // Helper function to get employee count text
  const getEmployeeCountText = (agent) => {
    if (!agent.selected_employees || agent.selected_employees.length === 0) {
      return 'paystubs'; // All employees - plural
    }
    return agent.selected_employees.length === 1 ? 'paystub' : 'paystubs';
  };

  // Helper function to format timesheet display for dashboard
  const formatTimesheetDisplay = (timesheetId, timesheetData = null) => {
    if (!timesheetId) return null;
    
    // If we have timesheet data from the agent, use it
    if (timesheetData) {
      const agentName = timesheetData.agent_name || 'Timesheet Report';
      const isRecurring = agentName.toLowerCase().includes('recurring') || 
                         timesheetData.type === 'recurring';
      const type = isRecurring ? 'Recurring Timesheet' : 'Adhoc Timesheet';
      
      // Format published date
      const publishedDate = timesheetData.published_at || timesheetData.created_at;
      let formattedDate = '';
      if (publishedDate) {
        const date = new Date(publishedDate);
        formattedDate = date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
      }
      
      return `${type}${formattedDate ? ` - ${formattedDate}` : ''}`;
    }
    
    // Fallback for when we don't have full timesheet data
    // Try to determine type from ID pattern or agent context
    return `Published Timesheet - ${timesheetId.substring(0, 8)}...`;
  };

  // Helper function to get actual pay period from timesheet data
  const getTimesheetPayPeriod = (agent) => {
    // Try to get timesheet data from the agent or stored data
    if (agent.timesheet_data) {
      const startDate = agent.timesheet_data.start_date;
      const endDate = agent.timesheet_data.end_date;
      
      if (startDate && endDate) {
        // Format dates without timezone conversion
        const formatDateFromString = (dateStr) => {
          const [year, month, day] = dateStr.split('-');
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                             'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
          const monthName = monthNames[parseInt(month) - 1];
          const dayNum = parseInt(day);
          
          // Add ordinal suffix
          const getDaySuffix = (day) => {
            if (day >= 11 && day <= 13) return 'th';
            switch (day % 10) {
              case 1: return 'st';
              case 2: return 'nd'; 
              case 3: return 'rd';
              default: return 'th';
            }
          };
          
          return `${monthName} ${dayNum}${getDaySuffix(dayNum)}`;
        };
        
        const startFormatted = formatDateFromString(startDate);
        const endFormatted = formatDateFromString(endDate);
        const year = startDate.split('-')[0];
        
        return `${startFormatted} - ${endFormatted}, ${year}`;
      }
    }
    
    // Fallback - try to extract from report_period
    if (agent.report_period) {
      return agent.report_period.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
    
    return 'Custom Period';
  };

  // Helper function to get paystub generation timing label
  const getPaystubGenerationTimingLabel = (days) => {
    if (days === 0) return 'Immediately after Timesheet is Published';
    if (days === 1) return '1 Day after Timesheet is Published';
    if (days === 2) return '2 Days after Timesheet is Published';
    return `${days} day${days !== 1 ? 's' : ''} after Timesheet is Published`;
  };

  // Helper function to get the "Runs Every" frequency for timesheet and paystub agents
  const getRunsEveryLabel = (agent) => {
    if ((agent.agent_type !== 'time_sheet' && agent.agent_type !== 'paystub_agent')) {
      return 'Not set';
    }
    
    // For paystub agents, use the default pay period from configuration
    if (agent.agent_type === 'paystub_agent' && agent.mode === 'paystub_recurring') {
      // Default to bi-weekly if not specified
      const frequency = agent.run_every_pay_period || 'bi_weekly';
      const labels = {
        'weekly': 'Weekly',
        'current_week': 'Weekly',
        'bi_weekly': 'Bi-weekly',
        'biweekly': 'Bi-weekly',
        'monthly': 'Monthly',
        'current_month': 'Monthly'
      };
      return labels[frequency] || 'Bi-weekly';
    }
    
    // For timesheet agents
    if (agent.agent_type === 'time_sheet' && !agent.run_every_pay_period) {
      return 'Not set';
    }
    
    const frequency = agent.run_every_pay_period;
    const labels = {
      'weekly': 'Weekly',
      'current_week': 'Weekly',
      'bi_weekly': 'Bi-weekly',
      'biweekly': 'Bi-weekly',
      'monthly': 'Monthly',
      'current_month': 'Monthly'
    };
    
    return labels[frequency] || frequency;
  };

  // Calculate next run time for recurring agents (non-timesheet)
  const calculateNextRunForRecurringAgent = (agent) => {
    if (agent.mode !== 'recurring' && agent.mode !== 'auto') {
      return null;
    }

    if (agent.agent_type === 'time_sheet') {
      return null; // Timesheet agents use their own calculation
    }

    try {
      // Get current time in business timezone (fallback to local if not loaded)
      const now = businessTime ? new Date(businessTime) : new Date();
      const scheduleTime = agent.post_time || '09:00';
      const [hours, minutes] = scheduleTime.split(':').map(Number);

      // Get selected days of the week - handle different data formats
      let daysOfWeek = agent.days_of_week || {};
      
      // Handle case where days_of_week might be stored as an array of strings (e.g., ['monday', 'tuesday'])
      if (Array.isArray(agent.days_of_week)) {
        daysOfWeek = {};
        const allDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
        allDays.forEach(day => {
          daysOfWeek[day] = agent.days_of_week.includes(day);
        });
      }
      
      // Add debug logging for social media agents
      if (agent.agent_type !== 'email' && agent.agent_type !== 'sms_agent' && agent.agent_type !== 'time_sheet' && agent.agent_type !== 'paystub_agent') {
        console.log('🔍 Social Media Agent Debug:', {
          agent_id: agent.id,
          agent_name: agent.agent_name,
          schedule_type: agent.schedule_type,
          days_of_week: agent.days_of_week,
          daysOfWeek: daysOfWeek,
          raw_agent: agent
        });
      }
      
      const selectedDays = Object.entries(daysOfWeek)
        .filter(([day, selected]) => selected)
        .map(([day]) => day);

      if (selectedDays.length === 0) {
        // Check if this is a monthly schedule instead of weekly
        if (agent.schedule_type === 'monthly' && agent.monthly_schedule) {
          return {
            nextRunDate: `Monthly: ${agent.monthly_schedule}`,
            lastRunDate: agent.last_manual_run ? formatDate(agent.last_manual_run) : 'Never run manually',
            selectedDays: 'Monthly Schedule'
          };
        }
        
        // Check if this is "All Days" schedule type
        if (agent.schedule_type === 'all_days') {
          // Handle "All Days" schedule with frequency
          const now = businessTime ? new Date(businessTime) : new Date();
          const scheduleTime = agent.post_time || '09:00';
          const [hours, minutes] = scheduleTime.split(':').map(Number);
          const frequency = parseInt(agent.frequency) || 24; // Default to 24 hours if not set
          
          // Calculate next run based on frequency
          let nextRun = new Date(now);
          
          if (frequency === 24) {
            // Daily - next run tomorrow at the scheduled time
            nextRun.setDate(nextRun.getDate() + 1);
            nextRun.setHours(hours, minutes, 0, 0);
          } else {
            // Every X hours - add frequency hours to current time
            nextRun = new Date(now.getTime() + (frequency * 60 * 60 * 1000));
          }
          
          const nextRunStr = nextRun.toLocaleDateString('en-US', { 
            weekday: 'short',
            month: 'short', 
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true 
          });

          const frequencyText = frequency === 24 ? 'Daily' : `Every ${frequency} hours`;

          return {
            nextRunDate: nextRunStr,
            lastRunDate: agent.last_manual_run ? formatDate(agent.last_manual_run) : 'Never run manually',
            selectedDays: frequencyText
          };
        }
        
        // Check if it's weekly but no specific days, assume daily (all days) as fallback
        if (agent.schedule_type === 'weekly' || !agent.schedule_type) {
          // Create a daily schedule as fallback
          const now = businessTime ? new Date(businessTime) : new Date();
          const scheduleTime = agent.post_time || '09:00';
          const [hours, minutes] = scheduleTime.split(':').map(Number);
          
          // Find next run (tomorrow at the scheduled time)
          const nextRun = new Date(now);
          nextRun.setDate(nextRun.getDate() + 1);
          nextRun.setHours(hours, minutes, 0, 0);
          
          const nextRunStr = nextRun.toLocaleDateString('en-US', { 
            weekday: 'short',
            month: 'short', 
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true 
          });

          return {
            nextRunDate: nextRunStr,
            lastRunDate: agent.last_manual_run ? formatDate(agent.last_manual_run) : 'Never run manually',
            selectedDays: 'Daily (All Days)'
          };
        }
        
        return {
          nextRunDate: 'No days selected',
          lastRunDate: agent.last_manual_run ? formatDate(agent.last_manual_run) : 'Never run manually'
        };
      }

      // Map day names to JavaScript day numbers (0 = Sunday, 1 = Monday, etc.)
      const dayMap = {
        'sunday': 0, 'monday': 1, 'tuesday': 2, 'wednesday': 3,
        'thursday': 4, 'friday': 5, 'saturday': 6
      };

      const selectedDayNumbers = selectedDays.map(day => dayMap[day.toLowerCase()]).filter(num => num !== undefined);

      if (selectedDayNumbers.length === 0) {
        return {
          nextRunDate: 'Invalid days configuration',
          lastRunDate: agent.last_manual_run ? formatDate(agent.last_manual_run) : 'Never run manually'
        };
      }

      // Find the next run date
      let nextRunDate = new Date(now);
      let daysToAdd = 0;
      const maxDaysToCheck = 14; // Check up to 2 weeks ahead

      while (daysToAdd < maxDaysToCheck) {
        const checkDate = new Date(now);
        checkDate.setDate(now.getDate() + daysToAdd);
        
        const dayOfWeek = checkDate.getDay();
        
        if (selectedDayNumbers.includes(dayOfWeek)) {
          // Set the time for this date
          checkDate.setHours(hours, minutes, 0, 0);
          
          // If it's today, make sure the time hasn't passed yet
          if (daysToAdd === 0 && checkDate <= now) {
            daysToAdd++;
            continue;
          }
          
          nextRunDate = checkDate;
          break;
        }
        
        daysToAdd++;
      }

      // Format the next run date
      const nextRunStr = nextRunDate.toLocaleDateString('en-US', { 
        weekday: 'short',
        month: 'short', 
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true 
      });

      return {
        nextRunDate: nextRunStr,
        lastRunDate: agent.last_manual_run ? formatDate(agent.last_manual_run) : 'Never run manually',
        selectedDays: selectedDays.map(day => day.charAt(0).toUpperCase() + day.slice(1)).join(', ')
      };

    } catch (error) {
      console.error('Error calculating next run for recurring agent:', error);
      return {
        nextRunDate: 'Error calculating next run',
        lastRunDate: agent.last_manual_run ? formatDate(agent.last_manual_run) : 'Never run manually'
      };
    }
  };

  const isReadyForNextPost = (agent) => {
    // Check if agent is adhoc or write mode, has no scheduled date/time, but has published a post before
    return (agent.mode === 'adhoc' || agent.mode === 'write') && 
           !agent.post_date && 
           !agent.post_time && 
           agent.last_post_published;
  };

  const formatAgentSchedule = (agent) => {
    // Check if agent is ready for next post
    if (isReadyForNextPost(agent)) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
          Ready for Next Post
        </span>
      );
    }
    
    // If agent has immediate flag set to true
    if (agent.immediate) {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
          Immediate
        </span>
      );
    }
    
    // If agent has post_date and post_time, combine them for user-friendly formatting
    if (agent.post_date && agent.post_time) {
      try {
        // Create a datetime string from post_date and post_time
        const datetimeString = `${agent.post_date}T${agent.post_time}:00`;
        return formatScheduledDate(datetimeString);
      } catch (error) {
        // Fallback to original format if parsing fails
        return `${agent.post_date} ${agent.post_time}`;
      }
    }
    
    // If no schedule info available
    return 'Not scheduled';
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      facebook: '📘',
      instagram: '📷',
      twitter: '𝕏',
      whatsapp: '💬'
    };
    return icons[platform] || '📱';
  };

  const getEnabledPlatforms = (socialPlatforms) => {
    if (!socialPlatforms) return [];
    return Object.entries(socialPlatforms)
      .filter(([platform, enabled]) => enabled)
      .map(([platform, enabled]) => platform);
  };

  const getWorkflowMode = (agent) => {
    // Check post_destination field first (newer agents)
    if (agent.post_destination) {
      switch (agent.post_destination) {
        case 'in_review':
          return 'In Review → Ready to Publish → Publish';
        case 'ready_to_publish':
          return 'Ready to Publish → Publish';
        case 'auto_post':
          return 'Directly Publish';
        default:
          return 'In Review → Ready to Publish → Publish';
      }
    }
    
    // Fallback to auto_post field (legacy agents)
    if (agent.auto_post) {
      return 'Directly Publish';
    } else {
      return 'In Review → Ready to Publish → Publish';
    }
  };

  const getImageDimensions = (platform, format) => {
    const socialMediaImageSettings = {
      facebook: {
        post: { width: 1080, height: 1080 },
        profile_picture: { width: 320, height: 320 },
        landscape: { width: 1080, height: 566 },
        square: { width: 1080, height: 1080 },
        story_reels: { width: 1080, height: 1920 },
        cover_photo: { width: 820, height: 312 }
      },
      instagram: {
        post: { width: 1080, height: 1080 },
        profile_picture: { width: 500, height: 500 },
        landscape: { width: 1080, height: 566 },
        square: { width: 1080, height: 1080 },
        story_reels: { width: 1080, height: 1920 },
        cover_photo: { width: 851, height: 315 }
      },
      twitter: {
        post: { width: 1080, height: 1920 },
        profile_picture: { width: 20, height: 20 },
        landscape: { width: 1920, height: 1080 },
        square: { width: 640, height: 640 },
        story_reels: { width: 1080, height: 1920 }
      },
      linkedin: {
        post: { width: 1080, height: 1080 },
        profile_picture: { width: 400, height: 400 },
        landscape: { width: 1280, height: 720 },
        square: { width: 720, height: 720 },
        cover_photo: { width: 1500, height: 500 }
      },
      whatsapp: {
        post: { width: 1080, height: 1080 }
      },
      tiktok: {
        post: { width: 1080, height: 1920 },
        profile_picture: { width: 200, height: 200 },
        landscape: { width: 1920, height: 1080 },
        square: { width: 1080, height: 1080 },
        story_reels: { width: 1080, height: 1920 }
      },
      email: {
        portrait: { width: 1080, height: 1920 },
        landscape: { width: 1280, height: 720 },
        square: { width: 600, height: 600 }
      },
      sms: {
        portrait: { width: 1080, height: 1920 },
        landscape: { width: 1280, height: 720 },
        square: { width: 600, height: 600 }
      }
    };

    const platformSettings = socialMediaImageSettings[platform];
    if (!platformSettings || !format) return null;
    
    const dimensions = platformSettings[format];
    return dimensions ? `${dimensions.width}×${dimensions.height}` : null;
  };

  // Helper function to format image option display
  const formatImageOption = (agent) => {
    if (!agent.image_option) {
      return 'AI Generate';
    }
    
    // For social media agents, show "Text only post" when no image is selected
    if (agent.agent_type === 'social_media' && agent.image_option === 'none') {
      return 'Text only post';
    }
    
    // For email agents, show "Text only email" when no image is selected
    if (agent.agent_type === 'email' && agent.image_option === 'none') {
      return 'Text only email';
    }
    
    // For SMS agents, show "No Images" when no image is selected
    if (agent.agent_type === 'sms_agent' && agent.image_option === 'none') {
      return 'No Images';
    }
    
    // For other cases, format normally
    return agent.image_option.replace('_', ' ');
  };

  // Helper function to get next scheduled holiday for email and SMS agents
  const getNextScheduledHoliday = (agent) => {
    if ((agent.agent_type !== 'email' && agent.agent_type !== 'sms_agent') || !agent.selected_holidays || agent.selected_holidays.length === 0) {
      return { nextRun: 'No holidays selected', holidayName: '' };
    }
    
    if (!holidays || holidays.length === 0) {
      return { nextRun: 'Loading holidays...', holidayName: '' };
    }
    
    const today = new Date();
    const postTime = agent.post_time || '09:00';
    let nextHoliday = null;
    let minDiff = Infinity;
    
    // Find the next upcoming holiday from selected holidays
    agent.selected_holidays.forEach(holidayId => {
      const holiday = holidays.find(h => h.id === holidayId);
      if (holiday) {
        const holidayDate = new Date(`${holiday.date}T${postTime}:00`);
        const timeDiff = holidayDate.getTime() - today.getTime();
        
        if (timeDiff > 0 && timeDiff < minDiff) {
          minDiff = timeDiff;
          nextHoliday = {
            name: holiday.name,
            date: holidayDate,
            postTime: postTime
          };
        }
      }
    });
    
    if (nextHoliday) {
      // Format date in user-friendly format like "Nov 23rd, 2025"
      const formatDate = (date) => {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                       'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const day = date.getDate();
        const month = months[date.getMonth()];
        const year = date.getFullYear();
        
        // Add ordinal suffix (st, nd, rd, th)
        const getOrdinalSuffix = (day) => {
          if (day > 3 && day < 21) return 'th';
          switch (day % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
          }
        };
        
        return `${month} ${day}${getOrdinalSuffix(day)}, ${year}`;
      };
      
      // Format time from 24-hour to 12-hour AM/PM format
      const formatTime = (time24) => {
        const [hours, minutes] = time24.split(':');
        const hour24 = parseInt(hours);
        const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
        const ampm = hour24 >= 12 ? 'PM' : 'AM';
        return `${hour12}:${minutes} ${ampm}`;
      };
      
      return {
        nextRun: `${formatDate(nextHoliday.date)} at ${formatTime(nextHoliday.postTime)}`,
        holidayName: nextHoliday.name
      };
    }
    
    const holidayCount = agent.selected_holidays.length;
    return { 
      nextRun: `No upcoming holidays this year (${holidayCount} selected)`, 
      holidayName: '' 
    };
  };

  // Helper function to format ChatGPT enabled status
  const formatChatGPTStatus = (agent) => {
    // Check all possible field names for ChatGPT formatting (prioritize the field actually saved)
    return (agent.use_chatgpt_formatting || agent.useChatGPTFormatting || agent.use_chatgpt_email_formatting) ? 'Yes' : 'No';
  };

  // Helper function to get customer full name
  const getCustomerFullName = (customer) => {
    if (!customer) return 'Unknown Customer';
    
    // Try different possible name fields
    if (customer.full_name) return customer.full_name;
    if (customer.name) return customer.name;
    if (customer.first_name && customer.last_name) {
      return `${customer.first_name} ${customer.last_name}`;
    }
    if (customer.first_name) return customer.first_name;
    if (customer.last_name) return customer.last_name;
    
    // Fallback to customer ID or email
    return customer.email || customer.id || 'Unknown Customer';
  };

  // Fetch employees for name mapping
  const fetchEmployees = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/employees`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const employeesData = await response.json();
        setEmployees(employeesData);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  // Fetch business timezone and info
  const fetchBusinessInfo = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/business-info`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const businessInfo = await response.json();
        
        // Set timezone - it might be in timezone field or need fallback
        const timezone = businessInfo.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
        setBusinessTimezone(timezone);
        
        // Parse the current time properly
        if (businessInfo.current_time) {
          const currentTime = new Date(businessInfo.current_time);
          if (!isNaN(currentTime.getTime())) {
            setBusinessTime(currentTime);
            console.log('Business timezone loaded:', timezone, 'Current time:', currentTime);
          } else {
            console.error('Invalid business time received:', businessInfo.current_time);
            setBusinessTime(new Date()); // Fallback to current time
          }
        } else {
          console.warn('No current_time in business info, using current time');
          setBusinessTime(new Date()); // Fallback if no current_time
        }
      } else {
        console.error('Failed to fetch business info:', response.status);
        // Fallback to system timezone and current time
        setBusinessTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
        setBusinessTime(new Date());
      }
    } catch (error) {
      console.error('Error fetching business info:', error);
      // Fallback to system timezone and current time
      setBusinessTimezone(Intl.DateTimeFormat().resolvedOptions().timeZone);
      setBusinessTime(new Date());
    }
  };

  // Fetch pay period settings for next run calculation
  const fetchPayPeriodSettings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/pay-period-settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const settings = await response.json();
        setPayPeriodSettings(settings);
      }
    } catch (error) {
      console.error('Error fetching pay period settings:', error);
    }
  };

  // Fetch timesheet configuration for original start date
  const fetchTimesheetConfig = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/timesheet-config`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const config = await response.json();
        return config.original_pay_period_start_date;
      }
    } catch (error) {
      console.error('Error fetching timesheet config:', error);
    }
    return null;
  };

  // Get employee name by ID
  const getEmployeeName = (employeeId) => {
    const employee = employees.find(emp => emp.id === employeeId);
    return employee ? (employee.full_name || employee.name || employeeId) : employeeId;
  };

  // Calculate next run date and pay period details for timesheet and paystub agents
  const calculatePayPeriodAndRunDate = (agent) => {
    if (!agent) {
      return null;
    }
    
    // Handle both timesheet and paystub agents
    const isTimesheetAgent = agent.agent_type === 'time_sheet' && agent.mode === 'recurring';
    const isPaystubAgent = agent.agent_type === 'paystub_agent' && agent.mode === 'paystub_recurring';
    
    if (!isTimesheetAgent && !isPaystubAgent) {
      return null;
    }

    if (!originalStartDate) {
      return {
        payPeriodRange: 'Set foundation date in Timesheet Configuration',
        nextRunDate: 'Cannot calculate without foundation date',
        daysAfter: agent.days_after_period_end || 1
      };
    }

    if (!businessTimezone || !businessTime) {
      // If business info isn't loaded yet, use current time as fallback
      const payPeriodType = agent.run_every_pay_period || 'weekly';
      const periodMapping = {
        'current_week': 'weekly',
        'weekly': 'weekly', 
        'bi_weekly': 'bi_weekly',
        'biweekly': 'bi_weekly',
        'current_month': 'monthly',
        'monthly': 'monthly'
      };
      
      const normalizedType = periodMapping[payPeriodType] || payPeriodType;
      
      return {
        payPeriodRange: `Loading ${normalizedType} period...`,
        nextRunDate: 'Loading next run date...',
        daysAfter: agent.days_after_period_end || 1
      };
    }

    try {
      // Handle different period type formats and map them correctly
      let payPeriodType = agent.run_every_pay_period || 'weekly';
      
      // Normalize period types
      const periodMapping = {
        'current_week': 'weekly',
        'weekly': 'weekly',
        'bi_weekly': 'bi_weekly',
        'biweekly': 'bi_weekly',
        'current_month': 'monthly',
        'monthly': 'monthly'
      };
      
      payPeriodType = periodMapping[payPeriodType] || payPeriodType;
      
      const daysAfterPeriodEnd = agent.days_after_period_end || 1;
      const scheduleTime = agent.schedule_time || '09:00';
      
      // Parse the original start date using business timezone
      const foundationStart = new Date(originalStartDate + 'T00:00:00');
      
      // Validate foundation date
      if (isNaN(foundationStart.getTime())) {
        console.error('Invalid foundation start date:', originalStartDate);
        return {
          payPeriodRange: 'Invalid foundation date',
          nextRunDate: 'Check Timesheet Configuration',
          daysAfter: agent.days_after_period_end || 1
        };
      }
      
      // Use business time with fallback to current time
      let now = new Date();
      if (businessTime && !isNaN(businessTime.getTime())) {
        now = new Date(businessTime);
      } else {
        console.warn('Using fallback current time for calculations');
        now = new Date();
      }
      
      let currentPeriodStart, nextPeriodEnd;

      if (payPeriodType === 'weekly') {
        // Calculate weeks since foundation date
        const msPerWeek = 7 * 24 * 60 * 60 * 1000;
        const weeksSinceFoundation = Math.floor((now - foundationStart) / msPerWeek);
        
        // Find the current pay period start
        currentPeriodStart = new Date(foundationStart);
        currentPeriodStart.setDate(foundationStart.getDate() + (weeksSinceFoundation * 7));
        
        const currentPeriodEnd = new Date(currentPeriodStart);
        currentPeriodEnd.setDate(currentPeriodStart.getDate() + 6); // 7 days - 1
        currentPeriodEnd.setHours(23, 59, 59, 999); // End of day
        
        // Check if we need to move to next period
        if (now > currentPeriodEnd) {
          // We're in the next weekly period
          currentPeriodStart.setDate(currentPeriodStart.getDate() + 7);
          const newPeriodEnd = new Date(currentPeriodStart);
          newPeriodEnd.setDate(currentPeriodStart.getDate() + 6);
          newPeriodEnd.setHours(23, 59, 59, 999);
          nextPeriodEnd = newPeriodEnd;
        } else {
          // We're still in current period, next run is for this period
          nextPeriodEnd = currentPeriodEnd;
        }
        
      } else if (payPeriodType === 'bi_weekly') {
        // Calculate bi-weeks since foundation date
        const msPerBiWeek = 14 * 24 * 60 * 60 * 1000;
        const biWeeksSinceFoundation = Math.floor((now - foundationStart) / msPerBiWeek);
        
        // Find the current pay period start
        currentPeriodStart = new Date(foundationStart);
        currentPeriodStart.setDate(foundationStart.getDate() + (biWeeksSinceFoundation * 14));
        
        const currentPeriodEnd = new Date(currentPeriodStart);
        currentPeriodEnd.setDate(currentPeriodStart.getDate() + 13); // 14 days - 1
        currentPeriodEnd.setHours(23, 59, 59, 999); // End of day
        
        // Check if we need to move to next period
        if (now > currentPeriodEnd) {
          // We're in the next bi-weekly period
          currentPeriodStart.setDate(currentPeriodStart.getDate() + 14);
          const newPeriodEnd = new Date(currentPeriodStart);
          newPeriodEnd.setDate(currentPeriodStart.getDate() + 13);
          newPeriodEnd.setHours(23, 59, 59, 999);
          nextPeriodEnd = newPeriodEnd;
        } else {
          // We're still in current period, next run is for this period
          nextPeriodEnd = currentPeriodEnd;
        }
        
      } else if (payPeriodType === 'monthly') {
        // Calculate months since foundation date
        const foundationMonth = foundationStart.getMonth();
        const foundationYear = foundationStart.getFullYear();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();
        
        const monthsElapsed = (currentYear - foundationYear) * 12 + (currentMonth - foundationMonth);
        
        // Find current period
        const currentPeriodStartYear = foundationYear + Math.floor((foundationMonth + monthsElapsed) / 12);
        const currentPeriodStartMonth = (foundationMonth + monthsElapsed) % 12;
        
        currentPeriodStart = new Date(currentPeriodStartYear, currentPeriodStartMonth, foundationStart.getDate());
        
        // End of current month period
        const currentPeriodEnd = new Date(currentPeriodStartYear, currentPeriodStartMonth + 1, 0); // Last day of month
        currentPeriodEnd.setHours(23, 59, 59, 999);
        
        // If current period has ended, get next period
        if (now > currentPeriodEnd) {
          currentPeriodStart = new Date(currentPeriodStartYear, currentPeriodStartMonth + 1, foundationStart.getDate());
          nextPeriodEnd = new Date(currentPeriodStartYear, currentPeriodStartMonth + 2, 0); // Next month end
          nextPeriodEnd.setHours(23, 59, 59, 999);
        } else {
          nextPeriodEnd = currentPeriodEnd;
        }
      } else {
        return {
          payPeriodRange: `${payPeriodType} period (custom)`,
          nextRunDate: `Next ${payPeriodType} period + ${daysAfterPeriodEnd} days at ${scheduleTime}`,
          daysAfter: daysAfterPeriodEnd
        };
      }

      // Calculate the next run date (period end + days after)
      const nextRunDate = new Date(nextPeriodEnd);
      nextRunDate.setDate(nextPeriodEnd.getDate() + daysAfterPeriodEnd);
      
      // Reset to beginning of day, then add schedule time
      nextRunDate.setHours(0, 0, 0, 0);
      const [hours, minutes] = scheduleTime.split(':');
      nextRunDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);

      // Format the pay period range
      const periodStartStr = currentPeriodStart.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: currentPeriodStart.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
      const periodEndStr = nextPeriodEnd.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: nextPeriodEnd.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });

      // Format the next run date
      const today = new Date();
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);

      let runDateStr;
      if (nextRunDate.toDateString() === today.toDateString()) {
        runDateStr = 'Today';
      } else if (nextRunDate.toDateString() === tomorrow.toDateString()) {
        runDateStr = 'Tomorrow';
      } else {
        runDateStr = nextRunDate.toLocaleDateString('en-US', { 
          weekday: 'short', 
          month: 'short', 
          day: 'numeric',
          year: nextRunDate.getFullYear() !== today.getFullYear() ? 'numeric' : undefined
        });
      }

      const timeStr = nextRunDate.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });

      return {
        payPeriodRange: `${periodStartStr} - ${periodEndStr}`,
        nextRunDate: `${runDateStr} at ${timeStr}`,
        daysAfter: daysAfterPeriodEnd,
        // Include raw dates for backend usage
        currentPeriodStart: currentPeriodStart,
        currentPeriodEnd: nextPeriodEnd
      };
    } catch (error) {
      console.error('Error calculating pay period and run date:', error);
      
      // Emergency fallback calculation using simple logic
      try {
        const payPeriodType = agent.run_every_pay_period || 'weekly';
        const now = new Date();
        const daysAfterPeriodEnd = agent.days_after_period_end || 1;
        
        let startDate, endDate;
        
        if (payPeriodType.includes('bi') || payPeriodType.includes('weekly')) {
          // Simple bi-weekly calculation
          const currentDate = now.getDate();
          const currentMonth = now.getMonth();
          const currentYear = now.getFullYear();
          
          // Simple approximation - start from 1st or 15th of month
          if (currentDate <= 14) {
            startDate = new Date(currentYear, currentMonth, 1);
            endDate = new Date(currentYear, currentMonth, 14);
          } else {
            startDate = new Date(currentYear, currentMonth, 15);
            endDate = new Date(currentYear, currentMonth + 1, 0); // Last day of month
          }
        } else {
          // Simple weekly calculation
          const dayOfWeek = now.getDay();
          const monday = new Date(now);
          monday.setDate(now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1));
          
          startDate = monday;
          endDate = new Date(monday);
          endDate.setDate(monday.getDate() + 6);
        }
        
        const formatSimpleDate = (date) => {
          const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                         'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
          const month = months[date.getMonth()];
          const day = date.getDate();
          const getDaySuffix = (day) => {
            if (day >= 11 && day <= 13) return 'th';
            switch (day % 10) {
              case 1: return 'st';
              case 2: return 'nd';
              case 3: return 'rd';
              default: return 'th';
            }
          };
          return `${month} ${day}${getDaySuffix(day)}`;
        };
        
        const startFormatted = formatSimpleDate(startDate);
        const endFormatted = formatSimpleDate(endDate);
        const year = endDate.getFullYear();
        
        return {
          payPeriodRange: `${startFormatted} - ${endFormatted}, ${year}`,
          nextRunDate: 'Calculation pending...',
          daysAfter: daysAfterPeriodEnd,
          currentPeriodStart: startDate,
          currentPeriodEnd: endDate
        };
        
      } catch (fallbackError) {
        console.error('Even fallback calculation failed:', fallbackError);
        return {
          payPeriodRange: 'Error calculating pay period',
          nextRunDate: 'Error calculating run date',
          daysAfter: agent.days_after_period_end || 1
        };
      }
    }
  };

  if (authLoading || pageLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-blue"></div>
      </div>
    );
  }

  // Show login prompt if not authenticated
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6">
          <div className="text-center">
            <BarChart3 className="mx-auto h-12 w-12 text-theme-blue" />
            <h2 className="mt-4 text-xl font-semibold text-gray-900">AI Agents Dashboard</h2>
            <p className="mt-2 text-sm text-gray-600">
              Please log in with manager or admin privileges to access the dashboard.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="mt-4 w-full bg-theme-blue text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header - Compact Design */}
          <div className="bg-white border-b border-gray-200">
            <div className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-3">
                    AI Agents Dashboard
                    <span className="text-xs font-normal text-gray-500">
                      Total: {allAgents.length} | Active: {allAgents.filter(agent => agent.is_active).length} | Recurring: {allAgents.filter(agent => agent.mode === 'auto' || agent.mode === 'recurring').length} | Adhoc: {allAgents.filter(agent => agent.mode === 'adhoc' || agent.mode === 'write').length}
                    </span>
                  </h2>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  {/* Status Filter */}
                  <select
                    value={statusFilter}
                    onChange={(e) => handleStatusFilterChange(e.target.value)}
                    className="text-white text-sm px-3 py-1.5 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1 transition-colors appearance-none pr-8"
                    style={{ 
                      backgroundColor: '#7c3aed',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 6px center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '14px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#6d28d9';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#7c3aed';
                    }}
                  >
                    <option value="all" style={{ backgroundColor: 'white', color: 'black' }}>All Agents</option>
                    <option value="active" style={{ backgroundColor: 'white', color: 'black' }}>Active Only</option>
                    <option value="paused" style={{ backgroundColor: 'white', color: 'black' }}>Paused Only</option>
                  </select>
                  
                  <select
                    value={agentTypeFilter}
                    onChange={(e) => handleAgentTypeFilterChange(e.target.value)}
                    className="text-white text-sm px-3 py-1.5 rounded-md transition-colors font-medium cursor-pointer appearance-none pr-8"
                    style={{ 
                      backgroundColor: 'var(--theme-primary)',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 6px center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '14px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = 'rgb(31, 153, 189)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'var(--theme-primary)';
                    }}
                  >
                    <option value="all" style={{ backgroundColor: 'white', color: 'black' }}>All Agent Types</option>
                    <option value="time_sheet" style={{ backgroundColor: 'white', color: 'black' }}>Timesheet Agents</option>
                    <option value="paystub_agent" style={{ backgroundColor: 'white', color: 'black' }}>Paystub Agents</option>
                    <option value="email" style={{ backgroundColor: 'white', color: 'black' }}>Email Agents</option>
                    <option value="sms_agent" style={{ backgroundColor: 'white', color: 'black' }}>SMS Agents</option>
                    <option value="marketing_agent" style={{ backgroundColor: 'white', color: 'black' }}>Marketing Agents</option>
                    <option value="social_media" style={{ backgroundColor: 'white', color: 'black' }}>Social Media Agents</option>
                  </select>
                  
                  <button
                    onClick={handleNewAgentClick}
                    className="bg-green-600 text-white text-sm px-3 py-1.5 rounded-md transition-colors hover:bg-green-700 flex items-center justify-center font-medium"
                  >
                    <Plus className="h-4 w-4 mr-1.5" />
                    New Agent
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="p-6">
            {/* Message Display */}
            {message.text && (
              <div className={`mb-6 rounded-md p-4 ${
                message.type === 'error' 
                  ? 'bg-red-50 border border-red-200' 
                  : 'bg-green-50 border border-green-200'
              }`}>
                <div className="flex">
                  <div className="flex-shrink-0">
                    {message.type === 'error' ? (
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    ) : (
                      <CheckCircle className="h-5 w-5 text-green-400" />
                    )}
                  </div>
                  <div className="ml-3">
                    <p className={`text-sm font-medium ${
                      message.type === 'error' ? 'text-red-800' : 'text-green-800'
                    }`}>
                      {message.text}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Agents List */}
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">Loading AI agents...</p>
              </div>
            ) : allAgents.length === 0 ? (
              <div className="text-center py-12">
                <Bot className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500 text-lg">No AI agents created yet</p>
                <p className="text-gray-400 text-sm mt-2">Create your first AI agent to get started</p>
                <a
                  href="/ai-agent-config"
                  className="inline-flex items-center mt-4 bg-theme-blue text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create AI Agent
                </a>
              </div>
            ) : (
              <div className="space-y-4">
                {agents.map((agent) => {
                  // Mode icon removed
                  
                  return (
                    <div key={agent.id} className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
                      <div className="p-6">
                        {/* Agent Header - Enhanced for SMS, Email, Marketing, Social Media, Paystub and Timesheet Agents */}
                        <div className={`${
                          (agent.agent_type === 'sms_agent' || agent.agent_type === 'email' || agent.agent_type === 'marketing_agent' || agent.agent_type === 'social_media' || agent.agent_type === 'paystub_agent' || agent.agent_type === 'time_sheet')
                            ? 'bg-gray-50 rounded-lg px-4 py-2 mb-3 border-b border-gray-200'
                            : 'mb-3'
                        }`}>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <div className="flex items-center space-x-2">
                                  <div className="flex flex-col">
                                    <h3 className="text-base font-semibold text-gray-900">
                                      {agent.agent_name || agent.name || `${getModeLabel(agent.mode, agent.agent_type, agent.selected_holidays)} Agent`}
                                    </h3>
                                    <div className="flex items-center space-x-2 mt-1">
                                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                                        agent.mode === 'auto' 
                                          ? 'bg-blue-100 text-blue-700' 
                                          : agent.mode === 'adhoc' 
                                          ? 'bg-orange-100 text-orange-700'
                                          : 'bg-purple-100 text-purple-700'
                                      }`}>
                                        {getModeLabel(agent.mode, agent.agent_type, agent.selected_holidays)} Agent
                                      </span>
                                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                                        agent.agent_type === 'time_sheet' ? 'bg-purple-100 text-purple-700' : 'bg-teal-100 text-teal-700'
                                      }`}>
                                        {agent.agent_type === 'time_sheet' ? 'Time Sheet' : 
                                         agent.agent_type === 'paystub_agent' ? 'Paystub' :
                                         agent.agent_type === 'email' || agent.agent_type === 'email_agent' ? 'Email' : 
                                         agent.agent_type === 'sms_agent' ? 'SMS' :
                                         agent.agent_type === 'marketing_agent' ? 'Marketing' :
                                         'Social Media'}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                                <span 
                                  className={`inline-flex items-center px-3 py-1 rounded text-xs font-medium ${
                                    agent.is_active 
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-gray-100 text-gray-800'
                                  }`}
                                >
                                  {agent.is_active ? 'Active' : 'Paused'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Agent Details - Improved Layout */}
                        <div className="bg-gray-50 rounded-lg p-4 mb-4">
                          {agent.agent_type === 'time_sheet' ? (
                            // Timesheet Agent Display
                            <div className="space-y-4">
                              {/* Row 1: Run Mode and Workflow Mode */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex flex-col">
                                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                    {agent.mode === 'adhoc' ? 'Run Mode' : 'Runs Every'}
                                  </span>
                                  <p className="text-sm text-gray-900 mt-1">
                                    {agent.mode === 'adhoc' ? 'Adhoc' : getRunsEveryLabel(agent)}
                                  </p>
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Workflow Mode</span>
                                  <p className="text-sm text-gray-900 mt-1">
                                    {getWorkflowMode(agent)}
                                  </p>
                                </div>
                              </div>
                              
                              {/* Row 2: Employees and Email Recipients */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex flex-col">
                                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Employees</span>
                                  <div className="mt-1">
                                    {agent.selected_employees && agent.selected_employees.length > 0 ? (
                                      <div className="text-sm text-gray-900">
                                        {employees.length > 0 ? (
                                          agent.selected_employees.map(empId => {
                                            const employee = employees.find(emp => emp.id === empId);
                                            return employee ? (employee.full_name || employee.name) : `Employee ${empId}`;
                                          }).join(', ')
                                        ) : (
                                          agent.selected_employees.join(', ')
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-sm text-gray-600">All employees</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email Recipients</span>
                                  <div className="mt-1">
                                    {agent.email_recipients && agent.email_recipients.length > 0 ? (
                                      <div className="text-sm text-gray-900">
                                        {agent.email_recipients.join(', ')}
                                      </div>
                                    ) : (
                                      <span className="text-sm text-gray-600">None configured</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Row 3: Configured Pay Period and Last Manual Run for Adhoc Agents */}
                              {agent.mode === 'adhoc' && (
                                <>
                                  {/* Divider line before Configured Pay Period */}
                                  <div className="border-t border-gray-200"></div>
                                  
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Configured Pay Period - Left Column - No background/border */}
                                    <div className="flex flex-col">
                                      <span className="text-xs font-semibold text-theme-blue uppercase tracking-wide mb-2">
                                        📅 Configured Pay Period
                                      </span>
                                      <div className="space-y-1">
                                        {agent.custom_start_date && agent.custom_end_date ? (
                                          // Show configured custom date range
                                          <div>
                                            <p className="text-sm font-medium text-blue-900">
                                              {(() => {
                                                // Parse date string directly to avoid timezone issues
                                                const [year, month, day] = agent.custom_start_date.split('-');
                                                const startDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                                                return startDate.toLocaleDateString('en-US', {
                                                  weekday: 'short',
                                                  year: 'numeric',
                                                  month: 'short',
                                                  day: 'numeric'
                                                });
                                              })()} - {(() => {
                                                // Parse date string directly to avoid timezone issues
                                                const [year, month, day] = agent.custom_end_date.split('-');
                                                const endDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                                                return endDate.toLocaleDateString('en-US', {
                                                  weekday: 'short',
                                                  year: 'numeric',
                                                  month: 'short',
                                                  day: 'numeric'
                                                });
                                              })()}
                                            </p>
                                            <p className="text-xs text-theme-blue mt-1">
                                              When manually run timesheet will be generated for above pay period
                                            </p>
                                          </div>
                                        ) : agent.report_period ? (
                                          // Show report period if no custom dates
                                          <div>
                                            <p className="text-sm font-medium text-blue-900">
                                              {agent.report_period === 'current_week' ? 'Current Week' :
                                               agent.report_period === 'last_week' ? 'Last Week' :
                                               agent.report_period === 'current_month' ? 'Current Month' :
                                               agent.report_period === 'last_month' ? 'Last Month' :
                                               agent.report_period.charAt(0).toUpperCase() + agent.report_period.slice(1)}
                                            </p>
                                            <p className="text-xs text-theme-blue mt-1">
                                              When manually run timesheet will be generated for above pay period
                                            </p>
                                          </div>
                                        ) : (
                                          // No configured period
                                          <div>
                                            <p className="text-sm text-gray-600">No pay period configured</p>
                                            <p className="text-xs text-theme-blue mt-1">
                                              When manually run timesheet will be generated for above pay period
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Last Manual Run - Right Column - No background/border */}
                                    <div className="flex flex-col">
                                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                                        Last Manual Run
                                      </span>
                                      <p className="text-sm text-gray-900">
                                        {(() => {
                                          // For adhoc agents, prioritize actual manual run time over scheduled dates
                                          if (agent.last_manual_run) {
                                            return new Date(agent.last_manual_run).toLocaleDateString('en-US', {
                                              year: 'numeric',
                                              month: 'short',
                                              day: 'numeric',
                                              hour: '2-digit',
                                              minute: '2-digit'
                                            });
                                          }
                                          
                                          if (agent.last_run_at) {
                                            return new Date(agent.last_run_at).toLocaleDateString('en-US', {
                                              year: 'numeric',
                                              month: 'short',
                                              day: 'numeric',
                                              hour: '2-digit',
                                              minute: '2-digit'
                                            });
                                          }
                                          
                                          return 'Never run manually';
                                        })()}
                                      </p>
                                    </div>
                                  </div>
                                </>
                              )}
                              
                              {/* Row 4: Next Run Schedule and Last Manual Run for Recurring Agents */}
                              {agent.mode === 'recurring' && (
                                <>
                                  {/* Divider line before Next Execution */}
                                  <div className="border-t border-gray-200"></div>
                                  
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Next Execution - Left Column - No background/border */}
                                    <div className="flex flex-col">
                                      <span className="text-xs font-semibold text-orange-600 uppercase tracking-wide mb-2">
                                        Next Execution
                                      </span>
                                      {(() => {
                                        const periodInfo = calculatePayPeriodAndRunDate(agent);
                                        if (!periodInfo) {
                                          return <p className="text-sm font-medium text-orange-900">Calculating...</p>;
                                        }
                                        
                                        return (
                                          <div className="space-y-1">
                                            <div>
                                              <p className="text-xs text-orange-600">Pay Period:</p>
                                              <p className="text-sm font-medium text-orange-800">{periodInfo.payPeriodRange}</p>
                                            </div>
                                            <div>
                                              <p className="text-xs text-orange-600">Next Run:</p>
                                              <p className="text-sm font-semibold text-orange-900">{periodInfo.nextRunDate}</p>
                                            </div>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                    
                                    {/* Last Manual Run - Right Column - No background/border */}
                                    <div className="flex flex-col">
                                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                                        Last Manual Run
                                      </span>
                                      <p className="text-sm text-gray-900">
                                        {agent.last_run_at ? 
                                          new Date(agent.last_run_at).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                          }) : 
                                          'Never run manually'
                                        }
                                      </p>
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          ) : agent.agent_type === 'paystub_agent' ? (
                            // Paystub Agent Display (similar structure to timesheet agents)
                            <div className="space-y-4">
                              {/* Row 1: Employees and Workflow Mode (Adhoc) OR Runs Every and Workflow Mode (Recurring) */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {agent.mode === 'paystub_adhoc' ? (
                                  // Adhoc mode: Show Employees + Workflow Mode
                                  <>
                                    <div className="flex flex-col">
                                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Employees</span>
                                      <div className="mt-1">
                                        {agent.selected_employees && agent.selected_employees.length > 0 ? (
                                          <div className="text-sm text-gray-900">
                                            {employees.length > 0 ? (
                                              agent.selected_employees.map(empId => {
                                                const employee = employees.find(emp => emp.id === empId);
                                                return employee ? (employee.full_name || employee.name) : `Employee ${empId}`;
                                              }).join(', ')
                                            ) : (
                                              agent.selected_employees.join(', ')
                                            )}
                                          </div>
                                        ) : (
                                          <span className="text-sm text-gray-600">All employees</span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex flex-col">
                                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Workflow Mode</span>
                                      <p className="text-sm text-gray-900 mt-1">
                                        {getWorkflowMode(agent)}
                                      </p>
                                    </div>
                                  </>
                                ) : (
                                  // Recurring mode: Show Runs Every + Workflow Mode
                                  <>
                                    <div className="flex flex-col">
                                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Runs Every</span>
                                      <p className="text-sm text-gray-900 mt-1">
                                        {getRunsEveryLabel(agent)}
                                      </p>
                                    </div>
                                    <div className="flex flex-col">
                                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Workflow Mode</span>
                                      <p className="text-sm text-gray-900 mt-1">
                                        {getWorkflowMode(agent)}
                                      </p>
                                    </div>
                                  </>
                                )}
                              </div>
                              
                              {/* Row 2: Employees and Paystub Will be Generated (Recurring only) */}
                              {agent.mode === 'paystub_recurring' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="flex flex-col">
                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Employees</span>
                                    <div className="mt-1">
                                      {agent.selected_employees && agent.selected_employees.length > 0 ? (
                                        <div className="text-sm text-gray-900">
                                          {employees.length > 0 ? (
                                            agent.selected_employees.map(empId => {
                                              const employee = employees.find(emp => emp.id === empId);
                                              return employee ? (employee.full_name || employee.name) : `Employee ${empId}`;
                                            }).join(', ')
                                          ) : (
                                            agent.selected_employees.join(', ')
                                          )}
                                        </div>
                                      ) : (
                                        <span className="text-sm text-gray-600">All employees</span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex flex-col">
                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Paystub Will be Generated</span>
                                    <p className="text-sm text-gray-900 mt-1">
                                      {getPaystubGenerationTimingLabel(agent.days_after_timesheet_published || 0)}
                                    </p>
                                  </div>
                                </div>
                              )}
                              
                              {/* Row 3: Selected Timesheet Details and Last Manual Run for Adhoc Agents */}
                              {(agent.mode === 'adhoc' || agent.mode === 'paystub_adhoc') && (
                                <>
                                  {/* Divider line before Selected Timesheet Details */}
                                  <div className="border-t border-gray-200"></div>
                                  
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Selected Timesheet Details - Left Column - No background/border */}
                                    <div className="flex flex-col">
                                      <span className="text-xs font-semibold text-theme-blue uppercase tracking-wide mb-2">
                                        📋 Selected Timesheet Details
                                      </span>
                                      <div className="space-y-1">
                                        {agent.selected_timesheet_id ? (
                                          <div>
                                            <p className="text-sm font-medium text-blue-900">
                                              {formatTimesheetDisplay(agent.selected_timesheet_id, agent.timesheet_data)}
                                            </p>
                                            <p className="text-xs text-blue-700 mt-1 font-medium">
                                              Pay Period: {getTimesheetPayPeriod(agent)}
                                            </p>
                                            <p className="text-xs text-theme-blue mt-2">
                                              When manually run, {getEmployeeCountText(agent)} will be generated for this specific Timesheet
                                            </p>
                                          </div>
                                        ) : (
                                          <div>
                                            <p className="text-sm text-gray-600">No Timesheet selected</p>
                                            <p className="text-xs text-theme-blue mt-1">
                                              Select a Timesheet to generate {getEmployeeCountText(agent)} for specific pay period
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    {/* Last Manual Run - Right Column - No background/border */}
                                    <div className="flex flex-col">
                                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                                        Last Manual Run
                                      </span>
                                      <p className="text-sm text-gray-900">
                                        {(() => {
                                          // For adhoc agents, prioritize actual manual run time over scheduled dates
                                          if (agent.last_manual_run) {
                                            return new Date(agent.last_manual_run).toLocaleDateString('en-US', {
                                              year: 'numeric',
                                              month: 'short',
                                              day: 'numeric',
                                              hour: '2-digit',
                                              minute: '2-digit'
                                            });
                                          }
                                          
                                          if (agent.last_run_at) {
                                            return new Date(agent.last_run_at).toLocaleDateString('en-US', {
                                              year: 'numeric',
                                              month: 'short',
                                              day: 'numeric',
                                              hour: '2-digit',
                                              minute: '2-digit'
                                            });
                                          }
                                          
                                          return 'Never run manually';
                                        })()}
                                      </p>
                                    </div>
                                  </div>
                                </>
                              )}
                              
                              {/* Row 5: Next Run Schedule and Last Manual Run for Recurring Agents */}
                              {agent.mode === 'paystub_recurring' && (
                                <>
                                  {/* Divider line before Next Execution */}
                                  <div className="border-t border-gray-200"></div>
                                  
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Next Execution - Left Column - No background/border */}
                                    <div className="flex flex-col">
                                      <span className="text-xs font-semibold text-orange-600 uppercase tracking-wide mb-2">
                                        Next Execution
                                      </span>
                                      {(() => {
                                        const periodInfo = calculatePayPeriodAndRunDate(agent);
                                        if (!periodInfo) {
                                          return <p className="text-sm font-medium text-orange-900">Calculating...</p>;
                                        }
                                        
                                        return (
                                          <div className="space-y-1">
                                            <div>
                                              <p className="text-xs text-orange-600">Pay Period:</p>
                                              <p className="text-sm font-medium text-orange-800">{periodInfo.payPeriodRange}</p>
                                            </div>
                                            <div>
                                              <p className="text-xs text-orange-600">Next Run:</p>
                                              <p className="text-sm font-semibold text-orange-900">{periodInfo.nextRunDate}</p>
                                            </div>
                                            <p className="text-xs text-orange-600 mt-1">
                                              Based on recurring timesheet agent schedule. When timesheet is published, paystub will be generated {agent.days_after_timesheet_published === 0 ? 'immediately' : agent.days_after_timesheet_published === 1 ? '1 day later' : '2 days later'}.
                                            </p>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                    
                                    {/* Last Manual Run - Right Column - No background/border */}
                                    <div className="flex flex-col">
                                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                                        Last Manual Run
                                      </span>
                                      <p className="text-sm text-gray-900">
                                        {agent.last_run_at ? 
                                          new Date(agent.last_run_at).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                          }) : 
                                          'Never run manually'
                                        }
                                      </p>
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          ) : agent.agent_type === 'marketing_agent' ? (
                            // Marketing Agent Display
                            <div className="space-y-3">
                              {/* Row 1: Topic and Format using ChatGPT */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="flex flex-col">
                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Topic</span>
                                    <p className="text-sm text-gray-900 mt-1">
                                      {getMarketingContentTypeDisplay(agent)}
                                    </p>
                                  </div>
                                  <div className="flex flex-col">
                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Format using ChatGPT</span>
                                    <div className="mt-1 flex flex-wrap gap-3">
                                      {(() => {
                                        const chatGPTStatus = getMarketingChatGPTStatus(agent);
                                        return (
                                          <>
                                            <div className="flex items-center">
                                              {chatGPTStatus.socialMedia ? (
                                                <svg className="h-4 w-4 text-green-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                              ) : (
                                                <svg className="h-4 w-4 text-red-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                </svg>
                                              )}
                                              <span className="text-xs text-gray-700">Social Media</span>
                                            </div>
                                            <div className="flex items-center">
                                              {chatGPTStatus.sms ? (
                                                <svg className="h-4 w-4 text-green-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                              ) : (
                                                <svg className="h-4 w-4 text-red-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                </svg>
                                              )}
                                              <span className="text-xs text-gray-700">SMS</span>
                                            </div>
                                            <div className="flex items-center">
                                              {chatGPTStatus.email ? (
                                                <svg className="h-4 w-4 text-green-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                              ) : (
                                                <svg className="h-4 w-4 text-red-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                </svg>
                                              )}
                                              <span className="text-xs text-gray-700">Email</span>
                                            </div>
                                          </>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                </div>

                              {/* Row 2: Selected Channels and Social Media Platforms */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex flex-col">
                                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Selected Channels</span>
                                  <p className="text-sm text-gray-900 mt-1">
                                    {agent.marketing_channels && agent.marketing_channels.length > 0 ? (
                                      agent.marketing_channels.map(channel => 
                                        channel === 'social_media' ? 'Social Media' : 
                                        channel === 'sms' ? 'SMS' :
                                        channel.charAt(0).toUpperCase() + channel.slice(1)
                                      ).join(', ')
                                    ) : 'None selected'}
                                  </p>
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Social Media Platforms</span>
                                  <div className="mt-1">
                                    {agent.marketing_channels && agent.marketing_channels.includes('social_media') && agent.marketing_social_platforms && Object.keys(agent.marketing_social_platforms).length > 0 ? (
                                      <div className="flex flex-wrap gap-2">
                                        {Object.entries(agent.marketing_social_platforms)
                                          .filter(([platform, enabled]) => enabled)
                                          .map(([platform]) => (
                                            <span key={platform} className="text-xs text-gray-700">
                                              {platform === 'twitter' ? 'X (Twitter)' : platform.charAt(0).toUpperCase() + platform.slice(1)}
                                            </span>
                                          ))}
                                        {Object.values(agent.marketing_social_platforms).every(enabled => !enabled) && (
                                          <span className="text-xs text-gray-600">No platforms selected</span>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-xs text-gray-600">
                                        {agent.marketing_channels && agent.marketing_channels.includes('social_media') ? 'No platforms configured' : 'Not applicable'}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Row 3: Content Workflow Mode and Image Options */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex flex-col">
                                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Content Review Workflow</span>
                                  <p className="text-sm text-gray-900 mt-1">
                                    In Review → Ready to Publish → Publish
                                  </p>
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Image Options</span>
                                  <div className="mt-1 flex flex-wrap gap-3">
                                    {(() => {
                                      const hasImageOption = agent.image_option && agent.image_option !== 'none';
                                      const smsIncludeImage = agent.marketing_sms_include_image || false;
                                      const emailIncludeImage = agent.marketing_email_include_image || false;
                                      
                                      return (
                                        <>
                                          <div className="flex items-center">
                                            {hasImageOption ? (
                                              <svg className="h-4 w-4 text-green-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                              </svg>
                                            ) : (
                                              <svg className="h-4 w-4 text-red-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                              </svg>
                                            )}
                                            <span className="text-xs text-gray-700">Social Media</span>
                                          </div>
                                          <div className="flex items-center">
                                            {smsIncludeImage ? (
                                              <svg className="h-4 w-4 text-green-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                              </svg>
                                            ) : (
                                              <svg className="h-4 w-4 text-red-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                              </svg>
                                            )}
                                            <span className="text-xs text-gray-700">SMS</span>
                                          </div>
                                          <div className="flex items-center">
                                            {emailIncludeImage ? (
                                              <svg className="h-4 w-4 text-green-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                              </svg>
                                            ) : (
                                              <svg className="h-4 w-4 text-red-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                              </svg>
                                            )}
                                            <span className="text-xs text-gray-700">Email</span>
                                          </div>
                                        </>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </div>

                              {/* Divider line */}
                              <div className="border-t border-gray-200"></div>

                              {/* Content Previews Removed */}

                              {/* Next Scheduled Run and Last Manual Run styled like SMS agents */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Next Scheduled Run - Left Column */}
                                <div className="flex flex-col">
                                  <span className="text-xs font-semibold text-theme-blue uppercase tracking-wide mb-2">
                                    Next Scheduled Run
                                  </span>
                                  <div className="space-y-1">
                                    <p className="text-sm font-medium text-blue-900">
                                      {agent.post_date ? (
                                        (() => {
                                          try {
                                            // Parse date string properly to avoid timezone issues
                                            // agent.post_date is in YYYY-MM-DD format, create date in local timezone
                                            const [year, month, day] = agent.post_date.split('-').map(num => parseInt(num));
                                            const scheduledDate = new Date(year, month - 1, day); // month is 0-indexed
                                            
                                            if (agent.post_time) {
                                              const [hours, minutes] = agent.post_time.split(':');
                                              scheduledDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                                            }
                                            const dateOptions = {
                                              weekday: 'short',
                                              month: 'short',
                                              day: 'numeric',
                                              year: 'numeric',
                                              ...(businessTimezone && { timeZone: businessTimezone })
                                            };
                                            const formattedDate = scheduledDate.toLocaleDateString('en-US', dateOptions);
                                            
                                            // Format time with AM/PM
                                            const timeString = agent.post_time || '09:00';
                                            const [hours, minutes] = timeString.split(':');
                                            const hour12 = parseInt(hours) % 12 || 12;
                                            const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
                                            const formattedTime = `${hour12}:${minutes} ${ampm}`;
                                            
                                            return `${formattedDate} ${formattedTime}`;
                                          } catch (error) {
                                            return `${agent.post_date} ${agent.post_time || '09:00'} AM`;
                                          }
                                        })()
                                      ) : (
                                        'Configured for Manual Run'
                                      )}
                                    </p>
                                  </div>
                                </div>
                                
                                {/* Last Manual Run - Right Column */}
                                <div className="flex flex-col">
                                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                                    Last Manual Run
                                  </span>
                                  <p className="text-sm text-gray-900">
                                    {agent.last_manual_run ? 
                                      new Date(agent.last_manual_run).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      }) : 
                                      'Never run manually'
                                    }
                                  </p>
                                </div>
                              </div>
                            </div>
                          ) : agent.agent_type === 'paystub_agent' ? (
                            // Paystub Agent Display (similar structure to timesheet agents)
                            <div className="space-y-4">
                              {/* Row 1: Run Mode and Workflow Mode */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex flex-col">
                                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                    {agent.mode === 'adhoc' ? 'Run Mode' : 'Runs Every'}
                                  </span>
                                  <p className="text-sm text-gray-900 mt-1">
                                    {agent.mode === 'adhoc' ? 'Adhoc' : getRunsEveryLabel(agent)}
                                  </p>
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Workflow Mode</span>
                                  <p className="text-sm text-gray-900 mt-1">
                                    {getWorkflowMode(agent)}
                                  </p>
                                </div>
                              </div>
                              
                              {/* Row 2: Employees */}
                              <div className="grid grid-cols-1 gap-4">
                                <div className="flex flex-col">
                                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Employees</span>
                                  <div className="mt-1">
                                    {agent.selected_employees && agent.selected_employees.length > 0 ? (
                                      <div className="text-sm text-gray-900">
                                        {employees.length > 0 ? (
                                          agent.selected_employees.map(empId => {
                                            const employee = employees.find(emp => emp.id === empId);
                                            return employee ? (employee.full_name || employee.name) : `Employee ${empId}`;
                                          }).join(', ')
                                        ) : (
                                          agent.selected_employees.join(', ')
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-sm text-gray-600">All employees</span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Row 3: Paystub Format and Days After Timesheet Published */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex flex-col">
                                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Paystub Format</span>
                                  <p className="text-sm text-gray-900 mt-1">
                                    {agent.paystub_format === 'pdf' ? 'PDF Document' :
                                     agent.paystub_format === 'email' ? 'Email Format' :
                                     'PDF Document'}
                                  </p>
                                </div>
                                {agent.mode === 'recurring' && (
                                  <div className="flex flex-col">
                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Days After Timesheet Published</span>
                                    <p className="text-sm text-gray-900 mt-1">
                                      {agent.days_after_timesheet_published ? `${agent.days_after_timesheet_published} day${agent.days_after_timesheet_published !== 1 ? 's' : ''}` : '1 day'}
                                    </p>
                                  </div>
                                )}
                              </div>
                              
                              {/* Row 4: Configured Pay Period and Last Manual Run for Adhoc Agents */}
                              {(agent.mode === 'adhoc' || agent.mode === 'paystub_adhoc') && (
                                <>
                                  {/* Divider line before Configured Pay Period */}
                                  <div className="border-t border-gray-200"></div>
                                  
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Configured Pay Period - Left Column - No background/border */}
                                    <div className="flex flex-col">
                                      <span className="text-xs font-semibold text-theme-blue uppercase tracking-wide mb-2">
                                        📅 Configured Pay Period
                                      </span>
                                      <div className="space-y-1">
                                        {agent.custom_start_date && agent.custom_end_date ? (
                                          // Show configured custom date range
                                          <div>
                                            <p className="text-sm font-medium text-blue-900">
                                              {(() => {
                                                // Parse date string directly to avoid timezone issues
                                                const [year, month, day] = agent.custom_start_date.split('-');
                                                const startDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                                                return startDate.toLocaleDateString('en-US', {
                                                  weekday: 'short',
                                                  year: 'numeric',
                                                  month: 'short',
                                                  day: 'numeric'
                                                });
                                              })()} - {(() => {
                                                // Parse date string directly to avoid timezone issues
                                                const [year, month, day] = agent.custom_end_date.split('-');
                                                const endDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
                                                return endDate.toLocaleDateString('en-US', {
                                                  weekday: 'short',
                                                  year: 'numeric',
                                                  month: 'short',
                                                  day: 'numeric'
                                                });
                                              })()}
                                            </p>
                                            <p className="text-xs text-theme-blue mt-1">
                                              When manually run paystub will be generated for above pay period
                                            </p>
                                          </div>
                                        ) : agent.report_period ? (
                                          // Show report period if no custom dates
                                          <div>
                                            <p className="text-sm font-medium text-blue-900">
                                              {agent.report_period === 'current_week' ? 'Current Week' :
                                               agent.report_period === 'last_week' ? 'Last Week' :
                                               agent.report_period === 'current_month' ? 'Current Month' :
                                               agent.report_period === 'last_month' ? 'Last Month' :
                                               agent.report_period.charAt(0).toUpperCase() + agent.report_period.slice(1)}
                                            </p>
                                            <p className="text-xs text-theme-blue mt-1">
                                              When manually run paystub will be generated for above pay period
                                            </p>
                                          </div>
                                        ) : (
                                          // No configured period
                                          <div>
                                            <p className="text-sm text-gray-600">No pay period configured</p>
                                            <p className="text-xs text-theme-blue mt-1">
                                              When manually run paystub will be generated for above pay period
                                            </p>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Last Manual Run - Right Column - No background/border */}
                                    <div className="flex flex-col">
                                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                                        Last Manual Run
                                      </span>
                                      <p className="text-sm text-gray-900">
                                        {(() => {
                                          // For adhoc agents, prioritize actual manual run time over scheduled dates
                                          if (agent.last_manual_run) {
                                            return new Date(agent.last_manual_run).toLocaleDateString('en-US', {
                                              year: 'numeric',
                                              month: 'short',
                                              day: 'numeric',
                                              hour: '2-digit',
                                              minute: '2-digit'
                                            });
                                          }
                                          
                                          if (agent.last_run_at) {
                                            return new Date(agent.last_run_at).toLocaleDateString('en-US', {
                                              year: 'numeric',
                                              month: 'short',
                                              day: 'numeric',
                                              hour: '2-digit',
                                              minute: '2-digit'
                                            });
                                          }
                                          
                                          return 'Never run manually';
                                        })()}
                                      </p>
                                    </div>
                                  </div>
                                </>
                              )}
                              
                              {/* Row 5: Next Run Schedule and Last Manual Run for Recurring Agents */}
                              {(agent.mode === 'recurring' || agent.mode === 'paystub_recurring') && (
                                <>
                                  {/* Divider line before Next Execution */}
                                  <div className="border-t border-gray-200"></div>
                                  
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* Next Execution - Left Column - No background/border */}
                                    <div className="flex flex-col">
                                      <span className="text-xs font-semibold text-orange-600 uppercase tracking-wide mb-2">
                                        Next Execution
                                      </span>
                                      {(() => {
                                        const periodInfo = calculatePayPeriodAndRunDate(agent);
                                        if (!periodInfo) {
                                          return <p className="text-sm font-medium text-orange-900">Calculating...</p>;
                                        }
                                        
                                        return (
                                          <div className="space-y-1">
                                            <div>
                                              <p className="text-xs text-orange-600">Pay Period:</p>
                                              <p className="text-sm font-medium text-orange-800">{periodInfo.payPeriodRange}</p>
                                            </div>
                                            <div>
                                              <p className="text-xs text-orange-600">Next Run:</p>
                                              <p className="text-sm font-semibold text-orange-900">{periodInfo.nextRunDate}</p>
                                            </div>
                                            <p className="text-xs text-orange-600 mt-1">
                                              Based on recurring timesheet agent schedule. When timesheet is published, paystub will be generated {agent.days_after_timesheet_published === 0 ? 'immediately' : agent.days_after_timesheet_published === 1 ? '1 day later' : '2 days later'}.
                                            </p>
                                          </div>
                                        );
                                      })()}
                                    </div>
                                    
                                    {/* Last Manual Run - Right Column - No background/border */}
                                    <div className="flex flex-col">
                                      <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                                        Last Manual Run
                                      </span>
                                      <p className="text-sm text-gray-900">
                                        {agent.last_run_at ? 
                                          new Date(agent.last_run_at).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                          }) : 
                                          'Never run manually'
                                        }
                                      </p>
                                    </div>
                                  </div>
                                </>
                              )}
                            </div>
                          ) : agent.agent_type === 'marketing_agent' ? (
                            // Marketing Agent Display
                            <div className="space-y-3">
                              {/* Row 1: Topic and Format using ChatGPT */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="flex flex-col">
                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Topic</span>
                                    <p className="text-sm text-gray-900 mt-1">
                                      {getMarketingContentTypeDisplay(agent)}
                                    </p>
                                  </div>
                                  <div className="flex flex-col">
                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Format using ChatGPT</span>
                                    <div className="mt-1 flex flex-wrap gap-3">
                                      {(() => {
                                        const chatGPTStatus = getMarketingChatGPTStatus(agent);
                                        return (
                                          <>
                                            <div className="flex items-center">
                                              {chatGPTStatus.socialMedia ? (
                                                <svg className="h-4 w-4 text-green-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                              ) : (
                                                <svg className="h-4 w-4 text-red-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                </svg>
                                              )}
                                              <span className="text-xs text-gray-700">Social Media</span>
                                            </div>
                                            <div className="flex items-center">
                                              {chatGPTStatus.sms ? (
                                                <svg className="h-4 w-4 text-green-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                              ) : (
                                                <svg className="h-4 w-4 text-red-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                </svg>
                                              )}
                                              <span className="text-xs text-gray-700">SMS</span>
                                            </div>
                                            <div className="flex items-center">
                                              {chatGPTStatus.email ? (
                                                <svg className="h-4 w-4 text-green-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                              ) : (
                                                <svg className="h-4 w-4 text-red-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                </svg>
                                              )}
                                              <span className="text-xs text-gray-700">Email</span>
                                            </div>
                                          </>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                </div>

                              {/* Row 2: Selected Channels and Social Media Platforms */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex flex-col">
                                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Selected Channels</span>
                                  <p className="text-sm text-gray-900 mt-1">
                                    {agent.marketing_channels && agent.marketing_channels.length > 0 ? (
                                      agent.marketing_channels.map(channel => 
                                        channel === 'social_media' ? 'Social Media' : 
                                        channel === 'sms' ? 'SMS' :
                                        channel.charAt(0).toUpperCase() + channel.slice(1)
                                      ).join(', ')
                                    ) : 'None selected'}
                                  </p>
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Social Media Platforms</span>
                                  <div className="mt-1">
                                    {agent.marketing_channels && agent.marketing_channels.includes('social_media') && agent.marketing_social_platforms && Object.keys(agent.marketing_social_platforms).length > 0 ? (
                                      <div className="flex flex-wrap gap-2">
                                        {Object.entries(agent.marketing_social_platforms)
                                          .filter(([platform, enabled]) => enabled)
                                          .map(([platform]) => (
                                            <span key={platform} className="text-xs text-gray-700">
                                              {platform === 'twitter' ? 'X (Twitter)' : platform.charAt(0).toUpperCase() + platform.slice(1)}
                                            </span>
                                          ))}
                                        {Object.values(agent.marketing_social_platforms).every(enabled => !enabled) && (
                                          <span className="text-xs text-gray-600">No platforms selected</span>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-xs text-gray-600">
                                        {agent.marketing_channels && agent.marketing_channels.includes('social_media') ? 'No platforms configured' : 'Not applicable'}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Row 3: Content Workflow Mode and Image Options */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex flex-col">
                                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Content Review Workflow</span>
                                  <p className="text-sm text-gray-900 mt-1">
                                    In Review → Ready to Publish → Publish
                                  </p>
                                </div>
                                <div className="flex flex-col">
                                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Image Options</span>
                                  <div className="mt-1 flex flex-wrap gap-3">
                                    {(() => {
                                      const hasImageOption = agent.image_option && agent.image_option !== 'none';
                                      const smsIncludeImage = agent.marketing_sms_include_image || false;
                                      const emailIncludeImage = agent.marketing_email_include_image || false;
                                      
                                      return (
                                        <>
                                          <div className="flex items-center">
                                            {hasImageOption ? (
                                              <svg className="h-4 w-4 text-green-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                              </svg>
                                            ) : (
                                              <svg className="h-4 w-4 text-red-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                              </svg>
                                            )}
                                            <span className="text-xs text-gray-700">Social Media</span>
                                          </div>
                                          <div className="flex items-center">
                                            {smsIncludeImage ? (
                                              <svg className="h-4 w-4 text-green-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                              </svg>
                                            ) : (
                                              <svg className="h-4 w-4 text-red-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                              </svg>
                                            )}
                                            <span className="text-xs text-gray-700">SMS</span>
                                          </div>
                                          <div className="flex items-center">
                                            {emailIncludeImage ? (
                                              <svg className="h-4 w-4 text-green-600 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                              </svg>
                                            ) : (
                                              <svg className="h-4 w-4 text-red-500 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                              </svg>
                                            )}
                                            <span className="text-xs text-gray-700">Email</span>
                                          </div>
                                        </>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </div>

                              {/* Divider line */}
                              <div className="border-t border-gray-200"></div>

                              {/* Content Previews Removed */}

                              {/* Next Scheduled Run and Last Manual Run styled like SMS agents */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Next Scheduled Run - Left Column */}
                                <div className="flex flex-col">
                                  <span className="text-xs font-semibold text-theme-blue uppercase tracking-wide mb-2">
                                    Next Scheduled Run
                                  </span>
                                  <div className="space-y-1">
                                    <p className="text-sm font-medium text-blue-900">
                                      {agent.post_date ? (
                                        (() => {
                                          try {
                                            // Parse date string properly to avoid timezone issues
                                            // agent.post_date is in YYYY-MM-DD format, create date in local timezone
                                            const [year, month, day] = agent.post_date.split('-').map(num => parseInt(num));
                                            const scheduledDate = new Date(year, month - 1, day); // month is 0-indexed
                                            
                                            if (agent.post_time) {
                                              const [hours, minutes] = agent.post_time.split(':');
                                              scheduledDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                                            }
                                            const dateOptions = {
                                              weekday: 'short',
                                              month: 'short',
                                              day: 'numeric',
                                              year: 'numeric',
                                              ...(businessTimezone && { timeZone: businessTimezone })
                                            };
                                            const formattedDate = scheduledDate.toLocaleDateString('en-US', dateOptions);
                                            
                                            // Format time with AM/PM
                                            const timeString = agent.post_time || '09:00';
                                            const [hours, minutes] = timeString.split(':');
                                            const hour12 = parseInt(hours) % 12 || 12;
                                            const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
                                            const formattedTime = `${hour12}:${minutes} ${ampm}`;
                                            
                                            return `${formattedDate} ${formattedTime}`;
                                          } catch (error) {
                                            return `${agent.post_date} ${agent.post_time || '09:00'} AM`;
                                          }
                                        })()
                                      ) : (
                                        'Configured for Manual Run'
                                      )}
                                    </p>
                                  </div>
                                </div>
                                
                                {/* Last Manual Run - Right Column */}
                                <div className="flex flex-col">
                                  <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                                    Last Manual Run
                                  </span>
                                  <p className="text-sm text-gray-900">
                                    {agent.last_manual_run ? 
                                      new Date(agent.last_manual_run).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                      }) : 
                                      'Never run manually'
                                    }
                                  </p>
                                </div>
                              </div>
                            </div>
                          ) : (
                            // Email, SMS vs Social Media Agent Display
                            <>
                              {agent.agent_type === 'email' ? (
                                // Email Agent Display (scheduled and recurring modes)
                                (agent.mode === 'auto' || agent.mode === 'recurring') && (
                                  <div className="space-y-4">
                                    {/* Row 1: Topic and Email Recipients */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div className="flex flex-col">
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Topic</span>
                                        <p className="text-sm text-gray-900 mt-1">
                                          {agent.selected_holidays && agent.selected_holidays.length > 0 ? 
                                            'Send Email for Selected Holidays' : 
                                            (agent.topic || 'No topic specified')
                                          }
                                        </p>
                                      </div>
                                      <div className="flex flex-col">
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email Recipients</span>
                                        <p className="text-sm text-gray-900 mt-1">
                                          {agent.email_type === 'single' ? 
                                            (() => {
                                              // Use same logic as SMS agents to get customer name
                                              const customerId = agent.selected_customer;
                                              if (customerId && customerData && customerData.customers && Array.isArray(customerData.customers)) {
                                                const customer = customerData.customers.find(c => 
                                                  String(c.id) === String(customerId)
                                                );
                                                if (customer) {
                                                  // Priority order: name field → first_name + last_name → full_name
                                                  if (customer.name && customer.name.trim()) {
                                                    return `Single Customer: ${customer.name}`;
                                                  }
                                                  if (customer.first_name || customer.last_name) {
                                                    return `Single Customer: ${customer.first_name || ''} ${customer.last_name || ''}`.trim();
                                                  }
                                                  if (customer.full_name && customer.full_name.trim()) {
                                                    return `Single Customer: ${customer.full_name}`;
                                                  }
                                                }
                                              }
                                              // Fallback
                                              return 'Single Customer';
                                            })() : 
                                            'All customers in database'
                                          }
                                        </p>
                                      </div>
                                    </div>
                                    
                                    {/* Row 2: Content Review Workflow and Image • ChatGPT */}
                                    {agent.selected_holidays && agent.selected_holidays.length > 0 ? (
                                      // Holiday Email Agent - 2 rows total
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="flex flex-col">
                                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Content Review Workflow</span>
                                          <p className="text-sm text-gray-900 mt-1">
                                            {getWorkflowMode(agent)}
                                          </p>
                                        </div>
                                        <div className="flex flex-col">
                                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Image • ChatGPT</span>
                                          <p className="text-sm text-gray-900 mt-1">
                                            {(() => {
                                              const imageStatus = (agent.image_option && agent.image_option !== 'none') ? 'Enabled' : 'Disabled';
                                              const chatgptStatus = 'Enabled'; // Always enabled for Holiday email agents
                                              return `${imageStatus} • ${chatgptStatus}`;
                                            })()}
                                          </p>
                                        </div>
                                      </div>
                                    ) : (
                                      // Recurring Email Agent - 2 rows total  
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="flex flex-col">
                                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Content Review Workflow</span>
                                          <p className="text-sm text-gray-900 mt-1">
                                            {getWorkflowMode(agent)}
                                          </p>
                                        </div>
                                        <div className="flex flex-col">
                                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Image • ChatGPT</span>
                                          <p className="text-sm text-gray-900 mt-1">
                                            {(() => {
                                              const imageStatus = (agent.image_option && agent.image_option !== 'none') ? 'Enabled' : 'Disabled';
                                              const chatgptStatus = 'Enabled'; // Always enabled for Recurring email agents
                                              return `${imageStatus} • ${chatgptStatus}`;
                                            })()}
                                          </p>
                                        </div>
                                      </div>
                                    )}

                                    {/* Divider line before Next/Last run sections */}
                                    <div className="border-t border-gray-200"></div>
                                    
                                    {/* Row 3: Next Scheduled Run and Last Manual Run for Holiday-based Email agents */}
                                    {agent.selected_holidays && agent.selected_holidays.length > 0 && (
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Next Scheduled Run - Left Column */}
                                        <div className="flex flex-col">
                                          <span className="text-xs font-semibold text-theme-blue uppercase tracking-wide mb-2">
                                            Next Scheduled Run
                                          </span>
                                          <div className="space-y-1">
                                            <p className="text-sm font-medium text-blue-900">
                                              {(() => {
                                                const holidayInfo = getNextScheduledHoliday(agent);
                                                return holidayInfo.holidayName || 'Next Holiday';
                                              })()}
                                            </p>
                                            <p className="text-sm text-blue-800">
                                              {(() => {
                                                const holidayInfo = getNextScheduledHoliday(agent);
                                                return holidayInfo.nextRun;
                                              })()}
                                            </p>
                                          </div>
                                        </div>
                                        
                                        {/* Last Manual Run - Right Column */}
                                        <div className="flex flex-col">
                                          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                                            Last Manual Run
                                          </span>
                                          <p className="text-sm text-gray-900">
                                            {agent.last_manual_run ? 
                                              new Date(agent.last_manual_run).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                              }) : 
                                              'Never run manually'
                                            }
                                          </p>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Row 3: Next Run and Last Run for Topic-based Recurring Email agents */}
                                    {agent.mode === 'recurring' && (!agent.selected_holidays || agent.selected_holidays.length === 0) && (
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Next Scheduled Run - Left Column */}
                                        <div className="flex flex-col">
                                          <span className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-2">
                                            Next Scheduled Run
                                          </span>
                                          <div className="space-y-1">
                                            <p className="text-sm font-medium text-green-900">
                                              {(() => {
                                                const runInfo = calculateNextRunForRecurringAgent(agent);
                                                return runInfo ? runInfo.nextRunDate : 'Not scheduled';
                                              })()}
                                            </p>
                                            {(() => {
                                              const runInfo = calculateNextRunForRecurringAgent(agent);
                                              return runInfo && runInfo.selectedDays ? (
                                                <p className="text-xs text-green-600">
                                                  {runInfo.selectedDays} at {agent.post_time || '09:00'}
                                                </p>
                                              ) : null;
                                            })()}
                                          </div>
                                        </div>
                                        
                                        {/* Last Manual Run - Right Column */}
                                        <div className="flex flex-col">
                                          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                                            Last Manual Run
                                          </span>
                                          <p className="text-sm text-gray-900">
                                            {agent.last_manual_run ? 
                                              new Date(agent.last_manual_run).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                              }) : 
                                              'Never run manually'
                                            }
                                          </p>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )
                              ) : agent.agent_type === 'sms_agent' ? (
                                // SMS Agent Display (scheduled and recurring modes)
                                (agent.mode === 'auto' || agent.mode === 'recurring') && (
                                  <div className="space-y-4">
                                    {/* Row 1: Topic and SMS Recipients */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div className="flex flex-col">
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Topic</span>
                                        <p className="text-sm text-gray-900 mt-1">
                                          {agent.selected_holidays && agent.selected_holidays.length > 0 ? 
                                            'Send SMS for Selected Holidays' : 
                                            (agent.topic || 'No topic specified')
                                          }
                                        </p>
                                      </div>
                                      <div className="flex flex-col">
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">SMS Recipients</span>
                                        <p className="text-sm text-gray-900 mt-1">
                                          {(agent.recipient_type === 'single' || agent.sms_type === 'single') ? 
                                            (() => {
                                              // Use same logic as view modal to get customer name
                                              const customerId = agent.selected_sms_customer || agent.selected_customer;
                                              if (customerId && customerData && customerData.customers && Array.isArray(customerData.customers)) {
                                                const customer = customerData.customers.find(c => 
                                                  String(c.id) === String(customerId)
                                                );
                                                if (customer) {
                                                  // Priority order: name field → first_name + last_name → full_name
                                                  if (customer.name && customer.name.trim()) {
                                                    return `Single Customer: ${customer.name}`;
                                                  }
                                                  if (customer.first_name || customer.last_name) {
                                                    return `Single Customer: ${customer.first_name || ''} ${customer.last_name || ''}`.trim();
                                                  }
                                                  if (customer.full_name && customer.full_name.trim()) {
                                                    return `Single Customer: ${customer.full_name}`;
                                                  }
                                                }
                                              }
                                              // Fallback
                                              return agent.customer_name ? `Single Customer: ${agent.customer_name}` : 'Single Customer: Selected Customer';
                                            })() : 
                                            'All customers in database'
                                          }
                                        </p>
                                      </div>
                                    </div>
                                    
                                    {/* Row 2: Content Review Workflow */}
                                    {agent.selected_holidays && agent.selected_holidays.length > 0 ? (
                                      // Holiday SMS Agent - 2 rows total
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="flex flex-col">
                                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Content Review Workflow</span>
                                          <p className="text-sm text-gray-900 mt-1">
                                            {getWorkflowMode(agent)}
                                          </p>
                                        </div>
                                        <div className="flex flex-col">
                                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Image • Link • ChatGPT</span>
                                          <p className="text-sm text-gray-900 mt-1">
                                            {(() => {
                                              const imageStatus = (agent.image_option && agent.image_option !== 'none') ? 'Enabled' : 'Disabled';
                                              const linkStatus = agent.enable_sms_link ? 'Enabled' : 'Disabled';
                                              const chatgptStatus = 'Enabled'; // Always enabled for Holiday agents
                                              return `${imageStatus} • ${linkStatus} • ${chatgptStatus}`;
                                            })()}
                                          </p>
                                        </div>
                                      </div>
                                    ) : (
                                      // Recurring SMS Agent - 2 rows total  
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="flex flex-col">
                                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Content Review Workflow</span>
                                          <p className="text-sm text-gray-900 mt-1">
                                            {getWorkflowMode(agent)}
                                          </p>
                                        </div>
                                        <div className="flex flex-col">
                                          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Image • Link • ChatGPT</span>
                                          <p className="text-sm text-gray-900 mt-1">
                                            {(() => {
                                              const imageStatus = (agent.image_option && agent.image_option !== 'none') ? 'Enabled' : 'Disabled';
                                              const linkStatus = agent.enable_sms_link ? 'Enabled' : 'Disabled';
                                              const chatgptStatus = 'Enabled'; // Always enabled for Recurring agents
                                              return `${imageStatus} • ${linkStatus} • ${chatgptStatus}`;
                                            })()}
                                          </p>
                                        </div>
                                      </div>
                                    )}

                                    {/* Divider line before Next/Last run sections */}
                                    <div className="border-t border-gray-200"></div>

                                    {/* Row 3: Next Scheduled Run and Last Manual Run for Holiday-based SMS agents */}
                                    {agent.selected_holidays && agent.selected_holidays.length > 0 && (
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Next Scheduled Run - Left Column */}
                                        <div className="flex flex-col">
                                          <span className="text-xs font-semibold text-theme-blue uppercase tracking-wide mb-2">
                                            Next Scheduled Run
                                          </span>
                                          <div className="space-y-1">
                                            <p className="text-sm font-medium text-blue-900">
                                              {(() => {
                                                const holidayInfo = getNextScheduledHoliday(agent);
                                                return holidayInfo.holidayName || 'Next Holiday';
                                              })()}
                                            </p>
                                            <p className="text-sm text-blue-800">
                                              {(() => {
                                                const holidayInfo = getNextScheduledHoliday(agent);
                                                return holidayInfo.nextRun;
                                              })()}
                                            </p>
                                          </div>
                                        </div>
                                        
                                        {/* Last Manual Run - Right Column */}
                                        <div className="flex flex-col">
                                          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                                            Last Manual Run
                                          </span>
                                          <p className="text-sm text-gray-900">
                                            {agent.last_manual_run ? 
                                              new Date(agent.last_manual_run).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                              }) : 
                                              'Never run manually'
                                            }
                                          </p>
                                        </div>
                                      </div>
                                    )}

                                    {/* Row 3: Next Run and Last Run for Topic-based Recurring SMS agents */}
                                    {(!agent.selected_holidays || agent.selected_holidays.length === 0) && (
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {/* Next Scheduled Run - Left Column */}
                                        <div className="flex flex-col">
                                          <span className="text-xs font-semibold text-purple-600 uppercase tracking-wide mb-2">
                                            Next Scheduled Run
                                          </span>
                                          <div className="space-y-1">
                                            <p className="text-sm font-medium text-purple-900">
                                              {(() => {
                                                const runInfo = calculateNextRunForRecurringAgent(agent);
                                                return runInfo ? runInfo.nextRunDate : 'Not scheduled';
                                              })()}
                                            </p>
                                            {(() => {
                                              const runInfo = calculateNextRunForRecurringAgent(agent);
                                              return runInfo && runInfo.selectedDays ? (
                                                <p className="text-xs text-purple-600">
                                                  {runInfo.selectedDays} at {agent.post_time || '09:00'}
                                                </p>
                                              ) : null;
                                            })()}
                                          </div>
                                        </div>
                                        
                                        {/* Last Manual Run - Right Column */}
                                        <div className="flex flex-col">
                                          <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                                            Last Manual Run
                                          </span>
                                          <p className="text-sm text-gray-900">
                                            {agent.last_manual_run ? 
                                              new Date(agent.last_manual_run).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                              }) : 
                                              'Never run manually'
                                            }
                                          </p>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )
                              ) : (
                                // Social Media Agent Display (recurring mode) - styled like SMS agent
                                (agent.mode === 'auto' || agent.mode === 'recurring') && (
                                  <div className="space-y-4">
                                    {/* Row 1: Topic and Image • Link • ChatGPT */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div className="flex flex-col">
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Topic</span>
                                        <p className="text-sm text-gray-900 mt-1">{agent.topic}</p>
                                      </div>
                                      <div className="flex flex-col">
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Image • Link • ChatGPT</span>
                                        <p className="text-sm text-gray-900 mt-1">
                                          {(() => {
                                            const imageStatus = (agent.image_option && agent.image_option !== 'none') ? 'Enabled' : 'Disabled';
                                            const linkStatus = agent.enable_social_link ? 'Enabled' : 'Disabled';
                                            const chatgptStatus = 'Enabled'; // Always enabled for recurring/adhoc social media agents
                                            return `${imageStatus} • ${linkStatus} • ${chatgptStatus}`;
                                          })()}
                                        </p>
                                      </div>
                                    </div>
                                    
                                    {/* Row 2: Workflow Mode and Social Media Platforms */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div className="flex flex-col">
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Workflow Mode</span>
                                        <p className="text-sm text-gray-900 mt-1">
                                          {getWorkflowMode(agent)}
                                        </p>
                                      </div>
                                      <div className="flex flex-col">
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Social Media Platforms</span>
                                        <p className="text-sm text-gray-900 mt-1">
                                          {getEnabledPlatforms(agent.social_platforms).length > 0 ? (
                                            getEnabledPlatforms(agent.social_platforms)
                                              .map(platform => platform === 'twitter' ? 'X (Twitter)' : platform.charAt(0).toUpperCase() + platform.slice(1))
                                              .join(', ')
                                          ) : (
                                            'None selected'
                                          )}
                                        </p>
                                      </div>
                                    </div>

                                    {/* Divider line before Next/Last run sections */}
                                    <div className="border-t border-gray-200"></div>
                                    
                                    {/* Row 3: Next Run and Last Run for Social Media Recurring Agents */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {/* Next Scheduled Run - Left Column */}
                                      <div className="flex flex-col">
                                        <span className="text-xs font-semibold text-purple-600 uppercase tracking-wide mb-2">
                                          Next Execution
                                        </span>
                                        <div className="space-y-1">
                                          <p className="text-sm font-medium text-purple-900">
                                            {(() => {
                                              const runInfo = calculateNextRunForRecurringAgent(agent);
                                              return runInfo ? runInfo.nextRunDate : 'Not scheduled';
                                            })()}
                                          </p>
                                          {(() => {
                                            const runInfo = calculateNextRunForRecurringAgent(agent);
                                            return runInfo && runInfo.selectedDays ? (
                                              <p className="text-xs text-purple-600">
                                                {runInfo.selectedDays} at {agent.post_time || '09:00'}
                                              </p>
                                            ) : null;
                                          })()}
                                        </div>
                                      </div>
                                      
                                      {/* Last Manual Run - Right Column - no border */}
                                      <div className="flex flex-col">
                                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                                          Last Manual Run
                                        </span>
                                        <p className="text-sm text-gray-900">
                                          {agent.last_manual_run ? 
                                            new Date(agent.last_manual_run).toLocaleDateString('en-US', {
                                              year: 'numeric',
                                              month: 'short',
                                              day: 'numeric',
                                              hour: '2-digit',
                                              minute: '2-digit'
                                            }) : 
                                            'Never run manually'
                                          }
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )
                              )}
                          
                              {agent.mode === 'adhoc' && (
                                agent.agent_type === 'email' ? (
                                  // Email Agent Adhoc Display
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Left Column */}
                                    <div className="space-y-3">
                                      <div className="flex flex-col">
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</span>
                                        <p className="text-sm text-gray-900 mt-1">Opt In Customers</p>
                                      </div>

                                      <div className="flex flex-col">
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Image Option</span>
                                        <p className="text-sm text-gray-900 mt-1 capitalize">{formatImageOption(agent)}</p>
                                      </div>
                                    </div>
                                    
                                    {/* Right Column */}
                                    <div className="space-y-3">
                                      <div className="flex flex-col">
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Use ChatGPT to Format Email</span>
                                        <p className="text-sm text-gray-900 mt-1">
                                          {formatChatGPTStatus(agent)}
                                        </p>
                                      </div>
                                      <div className="flex flex-col">
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Word Count</span>
                                        <p className="text-sm text-gray-900 mt-1">{agent.word_count || 'Not specified'}</p>
                                      </div>
                                    </div>
                                  </div>
                                ) : agent.agent_type === 'sms_agent' ? (
                                  // SMS Agent Adhoc Display - Custom Post Mode
                                  <div className="space-y-4">
                                    {/* Row 1: Topic and SMS Recipients */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div className="flex flex-col">
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Topic</span>
                                        <p className="text-sm text-gray-900 mt-1">Custom SMS</p>
                                      </div>
                                      <div className="flex flex-col">
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">SMS Recipients</span>
                                        <p className="text-sm text-gray-900 mt-1">
                                          {(agent.recipient_type === 'single' || agent.sms_type === 'single') ? 
                                            (() => {
                                              // Use same logic as view modal to get customer name
                                              const customerId = agent.selected_sms_customer || agent.selected_customer;
                                              if (customerId && customerData && customerData.customers && Array.isArray(customerData.customers)) {
                                                const customer = customerData.customers.find(c => 
                                                  String(c.id) === String(customerId)
                                                );
                                                if (customer) {
                                                  // Priority order: name field → first_name + last_name → full_name
                                                  if (customer.name && customer.name.trim()) {
                                                    return `Single Customer: ${customer.name}`;
                                                  }
                                                  if (customer.first_name || customer.last_name) {
                                                    return `Single Customer: ${customer.first_name || ''} ${customer.last_name || ''}`.trim();
                                                  }
                                                  if (customer.full_name && customer.full_name.trim()) {
                                                    return `Single Customer: ${customer.full_name}`;
                                                  }
                                                }
                                              }
                                              // Fallback
                                              return agent.customer_name ? `Single Customer: ${agent.customer_name}` : 'Single Customer: Selected Customer';
                                            })() : 
                                            'All customers in database'
                                          }
                                        </p>
                                      </div>
                                    </div>

                                    {/* Row 2: Content Review Workflow and Image • Link • ChatGPT Status */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div className="flex flex-col">
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Content Review Workflow</span>
                                        <p className="text-sm text-gray-900 mt-1">
                                          {getWorkflowMode(agent)}
                                        </p>
                                      </div>
                                      <div className="flex flex-col">
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Image • Link • ChatGPT</span>
                                        <p className="text-sm text-gray-900 mt-1">
                                          {(() => {
                                            const imageStatus = (agent.image_option && agent.image_option !== 'none') ? 'Enabled' : 'Disabled';
                                            const linkStatus = agent.enable_sms_link ? 'Enabled' : 'Disabled';
                                            const chatgptStatus = (agent.use_sms_chatgpt_formatting || agent.useSMSChatGPTFormatting || agent.use_chatgpt_sms_formatting) ? 'Enabled' : 'Disabled';
                                            return `${imageStatus} • ${linkStatus} • ${chatgptStatus}`;
                                          })()}
                                        </p>
                                      </div>
                                    </div>

                                    {/* SMS Content Template removed for Write SMS agents */}
                                  </div>
                                ) : (
                                  // Social Media Agent Adhoc Display - styled like SMS agent
                                  <div className="space-y-4">
                                    {/* Row 1: Topic and Image • Link • ChatGPT */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div className="flex flex-col">
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Topic</span>
                                        <p className="text-sm text-gray-900 mt-1">{agent.topic}</p>
                                      </div>
                                      <div className="flex flex-col">
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Image • Link • ChatGPT</span>
                                        <p className="text-sm text-gray-900 mt-1">
                                          {(() => {
                                            const imageStatus = (agent.image_option && agent.image_option !== 'none') ? 'Enabled' : 'Disabled';
                                            const linkStatus = agent.enable_social_link ? 'Enabled' : 'Disabled';
                                            const chatgptStatus = 'Enabled'; // Always enabled for adhoc social media agents
                                            return `${imageStatus} • ${linkStatus} • ${chatgptStatus}`;
                                          })()}
                                        </p>
                                      </div>
                                    </div>

                                    {/* Row 2: Workflow Mode and Social Media Platforms */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div className="flex flex-col">
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Workflow Mode</span>
                                        <p className="text-sm text-gray-900 mt-1">
                                          {getWorkflowMode(agent)}
                                        </p>
                                      </div>
                                      <div className="flex flex-col">
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Social Media Platforms</span>
                                        <p className="text-sm text-gray-900 mt-1">
                                          {getEnabledPlatforms(agent.social_platforms).length > 0 ? (
                                            getEnabledPlatforms(agent.social_platforms)
                                              .map(platform => platform === 'twitter' ? 'X (Twitter)' : platform.charAt(0).toUpperCase() + platform.slice(1))
                                              .join(', ')
                                          ) : (
                                            'None selected'
                                          )}
                                        </p>
                                      </div>
                                    </div>

                                    {/* Divider line before Next/Last run sections */}
                                    <div className="border-t border-gray-200"></div>

                                    {/* Next Run & Last Run for Adhoc Agents */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {/* Scheduled Next Run - Left Column */}
                                      <div className="flex flex-col">
                                        <span className="text-xs font-semibold text-green-600 uppercase tracking-wide mb-2">
                                          📅 Scheduled Next Run
                                        </span>
                                        <div className="space-y-1">
                                          {agent.post_date && agent.post_time ? (
                                            // Agent has scheduled date/time - show formatted date with past date logic
                                            (() => {
                                              try {
                                                const datetimeString = `${agent.post_date}T${agent.post_time}:00`;
                                                const date = new Date(datetimeString);
                                                const now = new Date();
                                                
                                                // Use business timezone for consistent display
                                                const timeZone = businessTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
                                                
                                                // Format date with business timezone
                                                const formattedDate = date.toLocaleDateString('en-US', {
                                                  weekday: 'short',
                                                  month: 'short',
                                                  day: 'numeric',
                                                  year: 'numeric',
                                                  timeZone: timeZone
                                                });
                                                
                                                // Format time with business timezone
                                                const formattedTime = date.toLocaleTimeString('en-US', { 
                                                  hour: 'numeric', 
                                                  minute: '2-digit',
                                                  hour12: true,
                                                  timeZone: timeZone
                                                });
                                                
                                                // Compare dates in business timezone
                                                const nowInBusinessTZ = new Date(now.toLocaleString('en-US', { timeZone }));
                                                const dateInBusinessTZ = new Date(date.toLocaleString('en-US', { timeZone }));
                                                
                                                if (dateInBusinessTZ < nowInBusinessTZ) {
                                                  // Past date - show suggestion message
                                                  return (
                                                    <div>
                                                      <p className="text-sm text-gray-500 line-through">
                                                        {formattedDate} at {formattedTime}
                                                      </p>
                                                      <p className="text-xs text-amber-600 mt-1 italic">
                                                        Schedule your next run or run it adhoc when you need
                                                      </p>
                                                    </div>
                                                  );
                                                } else {
                                                  // Future date - show normally
                                                  return (
                                                    <p className="text-sm font-medium text-green-900">
                                                      {formattedDate} at {formattedTime}
                                                    </p>
                                                  );
                                                }
                                              } catch (error) {
                                                console.error('Error formatting date:', error);
                                                return (
                                                  <p className="text-sm font-medium text-green-900">
                                                    {agent.post_date} at {agent.post_time}
                                                  </p>
                                                );
                                              }
                                            })()
                                          ) : (
                                            // No scheduled date/time - show manual options
                                            <div>
                                              {isReadyForNextPost(agent) ? (
                                                <div>
                                                  <p className="text-sm font-medium text-green-900">Ready for Next Post</p>
                                                  <p className="text-xs text-green-600 mt-1 italic">
                                                    Ready to create your next post. Click 'Run Agent' to generate new content.
                                                  </p>
                                                </div>
                                              ) : (
                                                <p className="text-sm font-medium text-green-900">Manual trigger only</p>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      
                                      {/* Last Manual Run - Right Column - no border */}
                                      <div className="flex flex-col">
                                        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                                          Last Manual Run
                                        </span>
                                        <p className="text-sm text-gray-900">
                                          {(() => {
                                            // For adhoc agents, prioritize actual manual run time over scheduled dates
                                            if (agent.last_manual_run) {
                                              return new Date(agent.last_manual_run).toLocaleDateString('en-US', {
                                                year: 'numeric',
                                                month: 'short',
                                                day: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                              });
                                            }
                                            
                                            // If no manual run time, check if there's a scheduled date in the past as fallback
                                            if (agent.post_date && agent.post_time) {
                                              try {
                                                // Parse date string properly to avoid timezone issues
                                                const [year, month, day] = agent.post_date.split('-').map(num => parseInt(num));
                                                const scheduledDate = new Date(year, month - 1, day); // month is 0-indexed
                                                const [hours, minutes] = agent.post_time.split(':');
                                                scheduledDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                                                const now = new Date();
                                                
                                                if (scheduledDate < now) {
                                                  // Past scheduled date - show as fallback
                                                  const formattedDate = scheduledDate.toLocaleDateString('en-US', {
                                                    weekday: 'short',
                                                    month: 'short',
                                                    day: 'numeric',
                                                    year: 'numeric'
                                                  });
                                                  return `${formattedDate} at ${formatTime(agent.post_time)}`;
                                                }
                                              } catch (error) {
                                                console.error('Error parsing scheduled date:', error);
                                              }
                                            }
                                            
                                            // Final fallback
                                            return 'Never run manually';
                                          })()}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  )
                                )}

                                {/* Social Media Platforms section removed - already displayed in Row 2 above */}
                                  
                                
                              
                          
                          {agent.mode === 'write' && (
                            <div className="space-y-4">
                              {/* Row 1: Topic and Email Recipients for Email Agents */}
                              {agent.agent_type === 'email' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="flex flex-col">
                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Topic</span>
                                    <p className="text-sm text-gray-900 mt-1">Custom Email</p>
                                  </div>
                                  <div className="flex flex-col">
                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email Recipients</span>
                                    <p className="text-sm text-gray-900 mt-1">
                                      {agent.email_type === 'single' ? 
                                        (() => {
                                          // Use same logic as SMS agents to get customer name
                                          const customerId = agent.selected_email_customer || agent.selected_customer;
                                          if (customerId && customerData && customerData.customers && Array.isArray(customerData.customers)) {
                                            const customer = customerData.customers.find(c => 
                                              String(c.id) === String(customerId)
                                            );
                                            if (customer) {
                                              // Priority order: name field → first_name + last_name → full_name
                                              if (customer.name && customer.name.trim()) {
                                                return `Single Customer: ${customer.name}`;
                                              }
                                              if (customer.first_name || customer.last_name) {
                                                return `Single Customer: ${customer.first_name || ''} ${customer.last_name || ''}`.trim();
                                              }
                                              if (customer.full_name && customer.full_name.trim()) {
                                                return `Single Customer: ${customer.full_name}`;
                                              }
                                            }
                                          }
                                          // Fallback
                                          return 'Single Customer';
                                        })() : 
                                        'All customers in database'
                                      }
                                    </p>
                                  </div>
                                </div>
                              )}
                              
                              {/* Row 1: Post Title and Image • Link • ChatGPT for Social Media Write Agents */}
                              {agent.agent_type !== 'email' && agent.agent_type !== 'time_sheet' && agent.agent_type !== 'sms_agent' && agent.agent_type !== 'paystub_agent' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="flex flex-col">
                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Post Title</span>
                                    <p className="text-sm text-gray-900 mt-1 font-medium">
                                      {agent.post_title || 'No title specified'}
                                    </p>
                                  </div>
                                  <div className="flex flex-col">
                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Image • Link • ChatGPT</span>
                                    <p className="text-sm text-gray-900 mt-1">
                                      {(() => {
                                        const imageStatus = (agent.image_option && agent.image_option !== 'none') ? 'Enabled' : 'Disabled';
                                        const linkStatus = agent.enable_social_link ? 'Enabled' : 'Disabled';
                                        // For write mode, use the actual use_chatgpt_formatting field value
                                        const chatgptStatus = (agent.use_chatgpt_formatting !== undefined ? agent.use_chatgpt_formatting : true) ? 'Enabled' : 'Disabled';
                                        return `${imageStatus} • ${linkStatus} • ${chatgptStatus}`;
                                      })()}
                                    </p>
                                  </div>
                                </div>
                              )}
                              
                              {/* Row 2: Workflow Mode and Social Media Platforms for Social Media Write Agents */}
                              {agent.agent_type !== 'email' && agent.agent_type !== 'time_sheet' && agent.agent_type !== 'sms_agent' && agent.agent_type !== 'paystub_agent' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="flex flex-col">
                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Workflow Mode</span>
                                    <p className="text-sm text-gray-900 mt-1">
                                      {getWorkflowMode(agent)}
                                    </p>
                                  </div>
                                  <div className="flex flex-col">
                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Social Media Platforms</span>
                                    <p className="text-sm text-gray-900 mt-1">
                                      {getEnabledPlatforms(agent.social_platforms).length > 0 ? (
                                        getEnabledPlatforms(agent.social_platforms)
                                          .map(platform => platform === 'twitter' ? 'X (Twitter)' : platform.charAt(0).toUpperCase() + platform.slice(1))
                                          .join(', ')
                                      ) : (
                                        'None selected'
                                      )}
                                    </p>
                                  </div>
                                </div>
                              )}
                              
                              {/* Row 2: Content Review Workflow and Image • ChatGPT for Email Agents */}
                              {agent.agent_type === 'email' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="flex flex-col">
                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Content Review Workflow</span>
                                    <p className="text-sm text-gray-900 mt-1">
                                      {getWorkflowMode(agent)}
                                    </p>
                                  </div>
                                  <div className="flex flex-col">
                                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Image • ChatGPT</span>
                                    <p className="text-sm text-gray-900 mt-1">
                                      {(() => {
                                        const imageStatus = (agent.image_option && agent.image_option !== 'none') ? 'Enabled' : 'Disabled';
                                        const chatgptStatus = agent.use_chatgpt_formatting ? 'Enabled' : 'Disabled';
                                        return `${imageStatus} • ${chatgptStatus}`;
                                      })()}
                                    </p>
                                  </div>
                                </div>
                              )}
                              
                              {/* SMS Agent Write Mode Display */}
                              {agent.agent_type === 'sms_agent' && (
                                <div className="space-y-4">
                                  {/* Row 1: Topic and SMS Recipients */}
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="flex flex-col">
                                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Topic</span>
                                      <p className="text-sm text-gray-900 mt-1">Custom SMS</p>
                                    </div>
                                    <div className="flex flex-col">
                                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">SMS Recipients</span>
                                      <p className="text-sm text-gray-900 mt-1">
                                        {(agent.recipient_type === 'single' || agent.sms_type === 'single') ? 
                                          (() => {
                                            // Use same logic as view modal to get customer name
                                            const customerId = agent.selected_sms_customer || agent.selected_customer;
                                            if (customerId && customerData && customerData.customers && Array.isArray(customerData.customers)) {
                                              const customer = customerData.customers.find(c => 
                                                String(c.id) === String(customerId)
                                              );
                                              if (customer) {
                                                // Priority order: name field → first_name + last_name → full_name
                                                if (customer.name && customer.name.trim()) {
                                                  return `Single Customer: ${customer.name}`;
                                                }
                                                if (customer.first_name || customer.last_name) {
                                                  return `Single Customer: ${customer.first_name || ''} ${customer.last_name || ''}`.trim();
                                                }
                                                if (customer.full_name && customer.full_name.trim()) {
                                                  return `Single Customer: ${customer.full_name}`;
                                                }
                                              }
                                            }
                                            // Fallback
                                            return agent.customer_name ? `Single Customer: ${agent.customer_name}` : 'Single Customer: Selected Customer';
                                          })() : 
                                          'All customers in database'
                                        }
                                      </p>
                                    </div>
                                  </div>
                                  
                                  {/* Row 2: Content Review Workflow and Image • Link • ChatGPT Status */}
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="flex flex-col">
                                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Content Review Workflow</span>
                                      <p className="text-sm text-gray-900 mt-1">
                                        {getWorkflowMode(agent)}
                                      </p>
                                    </div>
                                    <div className="flex flex-col">
                                      <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Image • Link • ChatGPT</span>
                                      <p className="text-sm text-gray-900 mt-1">
                                        {(() => {
                                          const imageStatus = (agent.image_option && agent.image_option !== 'none') ? 'Enabled' : 'Disabled';
                                          const linkStatus = (agent.enable_sms_link || agent.sms_link) ? 'Enabled' : 'Disabled';
                                          const chatgptStatus = (agent.use_sms_chatgpt_formatting || agent.useSMSChatGPTFormatting || agent.use_chatgpt_sms_formatting) ? 'Enabled' : 'Disabled';
                                          return `${imageStatus} • ${linkStatus} • ${chatgptStatus}`;
                                        })()}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}
                              
                              {/* Content Preview section removed for Email agents */}
                              
                              {/* Image Option and Social Media Platforms moved to after Post Title row */}
                              
                              {/* Divider line before Next/Last run sections for SMS and Email agents */}
                              {(agent.agent_type === 'sms_agent' || agent.agent_type === 'email') && (
                                <div className="border-t border-gray-200"></div>
                              )}
                              
                              {/* Next Run & Last Run for Scheduled Write Mode Agents */}
                              {agent.post_date && agent.post_time && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {/* Scheduled Next Run - Left Column */}
                                  <div className="flex flex-col">
                                    <span className="text-xs font-semibold text-theme-blue uppercase tracking-wide mb-2">
                                      Next Scheduled Run
                                    </span>
                                    <div className="space-y-1">
                                      {(() => {
                                        try {
                                          const datetimeString = `${agent.post_date}T${agent.post_time}:00`;
                                          const date = new Date(datetimeString);
                                          const now = new Date();
                                          
                                          // Use business timezone for consistent display
                                          const timeZone = businessTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
                                          
                                          // Format date with business timezone
                                          const formattedDate = date.toLocaleDateString('en-US', {
                                            weekday: 'short',
                                            month: 'short',
                                            day: 'numeric',
                                            year: 'numeric',
                                            timeZone: timeZone
                                          });
                                          
                                          // Format time with business timezone
                                          const formattedTime = date.toLocaleTimeString('en-US', { 
                                            hour: 'numeric', 
                                            minute: '2-digit',
                                            hour12: true,
                                            timeZone: timeZone
                                          });
                                          
                                          // Compare dates in business timezone
                                          const nowInBusinessTZ = new Date(now.toLocaleString('en-US', { timeZone }));
                                          const dateInBusinessTZ = new Date(date.toLocaleString('en-US', { timeZone }));
                                          
                                          if (dateInBusinessTZ < nowInBusinessTZ) {
                                            // Past date - show suggestion message
                                            return (
                                              <div>
                                                <p className="text-sm text-gray-500 line-through">
                                                  {formattedDate} at {formattedTime}
                                                </p>
                                                <p className="text-xs text-amber-600 mt-1 italic">
                                                  Schedule your next run or run it adhoc when you need
                                                </p>
                                              </div>
                                            );
                                          } else {
                                            // Future date - show normally
                                            return (
                                              <p className="text-sm font-medium text-blue-900">
                                                {formattedDate} at {formattedTime}
                                              </p>
                                            );
                                          }
                                        } catch (error) {
                                          console.error('Error formatting date:', error);
                                          return (
                                            <p className="text-sm font-medium text-blue-900">
                                              {agent.post_date} at {agent.post_time}
                                            </p>
                                          );
                                        }
                                      })()}
                                    </div>
                                  </div>
                                  
                                  {/* Last Manual Run - Right Column */}
                                  <div className="flex flex-col">
                                    <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                                      Last Manual Run
                                    </span>
                                    <p className="text-sm text-gray-900">
                                      {(() => {
                                        // For adhoc agents, prioritize actual manual run time over scheduled dates
                                        if (agent.last_manual_run) {
                                          return new Date(agent.last_manual_run).toLocaleDateString('en-US', {
                                            year: 'numeric',
                                            month: 'short',
                                            day: 'numeric',
                                            hour: '2-digit',
                                            minute: '2-digit'
                                          });
                                        }
                                        
                                        // If no manual run time, check if there's a scheduled date in the past as fallback
                                        if (agent.post_date && agent.post_time) {
                                          try {
                                            // Parse date string properly to avoid timezone issues
                                            const [year, month, day] = agent.post_date.split('-').map(num => parseInt(num));
                                            const scheduledDate = new Date(year, month - 1, day); // month is 0-indexed
                                            const [hours, minutes] = agent.post_time.split(':');
                                            scheduledDate.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                                            const now = new Date();
                                            
                                            if (scheduledDate < now) {
                                              // Past scheduled date - show as fallback
                                              const formattedDate = scheduledDate.toLocaleDateString('en-US', {
                                                weekday: 'short',
                                                month: 'short',
                                                day: 'numeric',
                                                year: 'numeric'
                                              });
                                              return `${formattedDate} at ${formatTime(agent.post_time)}`;
                                            }
                                          } catch (error) {
                                            console.error('Error parsing scheduled date:', error);
                                          }
                                        }
                                        
                                        // Final fallback
                                        return 'Never run manually';
                                      })()}
                                    </p>
                                  </div>
                                </div>
                              )}
                              
                              {/* Social Media Platforms section removed - already displayed in Row 2 above */}
                              
                              {/* Last Manual Run - Only for Email Write Agents without scheduled date/time */}
                              {agent.agent_type === 'email' && (!agent.post_date || !agent.post_time) && (
                                <div className="bg-gradient-to-r from-gray-50 to-slate-50 border border-gray-200 rounded-lg p-4">
                                  <div className="flex flex-col">
                                    <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Last Manual Run</span>
                                    <p className="text-sm text-gray-900 mt-1">
                                      {agent.last_manual_run ? formatDate(agent.last_manual_run) : 'Never run manually'}
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                          
                            </>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                          <button
                            onClick={() => setSelectedAgent(agent)}
                            className="flex items-center px-3 py-2 text-theme-blue border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors text-sm font-medium"
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </button>
                          
                          <button
                            onClick={() => handleEditAgent(agent)}
                            className="flex items-center px-3 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
                          >
                            <Edit2 className="h-4 w-4 mr-1" />
                            Edit
                          </button>

                          {agent.is_active && (
                            <button
                              onClick={() => handleRunAgent(agent)}
                              className="flex items-center px-3 py-2 text-green-600 border border-green-300 rounded-lg hover:bg-green-50 transition-colors text-sm font-medium"
                            >
                              <Play className="h-4 w-4 mr-1" />
                              Run Agent
                            </button>
                          )}

                          <button
                            onClick={() => toggleAgentStatus(agent.id, agent.is_active)}
                            disabled={processingAgent === agent.id}
                            className={`flex items-center px-3 py-2 border rounded-lg transition-colors text-sm font-medium ${
                              agent.is_active
                                ? 'text-orange-600 border-orange-300 hover:bg-orange-50'
                                : 'text-green-600 border-green-300 hover:bg-green-50'
                            }`}
                          >
                            {processingAgent === agent.id ? (
                              <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                            ) : agent.is_active ? (
                              <Pause className="h-4 w-4 mr-1" />
                            ) : (
                              <Play className="h-4 w-4 mr-1" />
                            )}
                            {agent.is_active ? 'Pause' : 'Activate'}
                          </button>

                          <button
                            onClick={() => openDeleteModal(agent)}
                            disabled={processingAgent === agent.id}
                            className="flex items-center px-3 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors text-sm font-medium"
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Agent Type Selection Modal */}
      {showAgentTypeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
            {/* Sticky Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0" style={{ backgroundColor: 'var(--theme-primary)' }}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-white">Select Agent Type</h3>
                <button
                  onClick={() => setShowAgentTypeModal(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <p className="text-sm text-gray-600 mb-6">
                Choose the type of AI agent you want to create:
              </p>
              
              <div className="space-y-3">
                {agentTypes.map((type) => (
                  <button
                    key={type.value}
                    onClick={() => handleAgentTypeSelect(type)}
                    className="w-full text-left p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors group"
                  >
                    <div className="flex items-start">
                      <div className="flex-shrink-0 mr-3 mt-1">
                        {type.value === 'social_media' ? (
                          <Share2 className="h-5 w-5 text-theme-blue" />
                        ) : (
                          <Clock className="h-5 w-5 text-purple-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900 group-hover:text-blue-700">
                          {type.label}
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">
                          {type.description}
                        </p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Sticky Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex-shrink-0" style={{ backgroundColor: '#f8fafc' }}>
              <div className="flex justify-end">
                <button
                  onClick={() => setShowAgentTypeModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Agent Details Modal - User Friendly Display */}
      {selectedAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[90vh] flex flex-col">
            {/* Sticky Header - Compact */}
            <div className="px-6 py-3 border-b border-gray-200 rounded-t-lg" style={{ backgroundColor: 'var(--theme-primary)' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-white bg-opacity-20">
                    {selectedAgent.agent_type === 'time_sheet' ? (
                      <Clock className="h-5 w-5 text-white" />
                    ) : (
                      <Share2 className="h-5 w-5 text-white" />
                    )}
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-white">
                      {selectedAgent.name || selectedAgent.agent_name || 'Unnamed Agent'}
                    </h3>
                    {/* Agent type and status removed from header */}
                  </div>
                </div>
                <button
                  onClick={() => setSelectedAgent(null)}
                  className="text-white hover:text-red-200 transition-colors p-1 rounded-full hover:bg-white hover:bg-opacity-20"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-6 py-6">
              
              {/* Debug Information section removed */}
              
              {/* Agent Configuration Display */}
              <div className="space-y-4">
                
                {/* Conditional rendering based on agent type */}
                {selectedAgent.agent_type === 'time_sheet' ? (
                  // Timesheet Agent View
                  <>
                    {/* Timesheet Agent Overview */}
                    <section>
                      <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                        <Clock className="h-5 w-5 text-theme-blue mr-2" />
                        Timesheet Agent Overview
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Agent Name</label>
                            <p className="text-sm text-gray-900 mt-1 font-medium">{selectedAgent.name || selectedAgent.agent_name}</p>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Agent Type</label>
                            <p className="text-sm text-gray-900 mt-1">Timesheet</p>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</label>
                            <p className="text-sm text-gray-900 mt-1 font-medium capitalize">
                              {selectedAgent.is_active ? 'Active' : 'Inactive'}
                            </p>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Mode</label>
                            <p className="text-sm text-gray-900 mt-1">{getModeLabel(selectedAgent.mode, selectedAgent.agent_type, selectedAgent.selected_holidays)}</p>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Workflow Mode</label>
                            <p className="text-sm text-gray-900 mt-1">
                              {getWorkflowMode(selectedAgent)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* Scheduling & Timing */}
                    {selectedAgent.mode === 'recurring' && selectedAgent.is_active && (
                      <section>
                        <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                          <Clock className="h-5 w-5 text-theme-blue mr-2" />
                          Scheduling & Timing
                          <button
                            onClick={() => {
                              fetchEmployees();
                              fetchPayPeriodSettings();
                              fetchTimesheetConfig().then(startDate => setOriginalStartDate(startDate));
                            }}
                            className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 transition-colors"
                          >
                            Refresh Data
                          </button>
                        </h4>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {selectedAgent.schedule_time && (
                              <div>
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Schedule Time</label>
                                <p className="text-sm text-gray-900 mt-1 font-medium">{selectedAgent.schedule_time}</p>
                              </div>
                            )}
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Next Run Schedule</label>
                              {(() => {
                                const periodInfo = calculatePayPeriodAndRunDate(selectedAgent);
                                if (!periodInfo) {
                                  return <p className="text-sm text-gray-900 mt-1 font-medium">Calculating...</p>;
                                }
                                
                                return (
                                  <div className="mt-1">
                                    <p className="text-sm font-medium text-gray-900">{periodInfo.nextRunDate}</p>
                                    <p className="text-xs text-gray-600 mt-1">Pay Period: {periodInfo.payPeriodRange}</p>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      </section>
                    )}

                    {/* Employee Selection */}
                    <section>
                      <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                        <Users className="h-5 w-5 text-purple-600 mr-2" />
                        Selected Employees
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex flex-wrap gap-2">
                          {selectedAgent.selected_employees && selectedAgent.selected_employees.length > 0 ? (
                            selectedAgent.selected_employees.map((employeeId, index) => (
                              <span 
                                key={index}
                                className="inline-flex items-center px-3 py-2 rounded text-sm font-medium bg-purple-100 text-purple-700 border border-purple-200"
                              >
                                {getEmployeeName(employeeId)}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-gray-500">All employees selected</span>
                          )}
                        </div>
                      </div>
                    </section>

                    {/* Timesheet Configuration */}
                    <section>
                      <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                        <Calendar className="h-5 w-5 text-green-600 mr-2" />
                        Timesheet Configuration
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {selectedAgent.mode === 'recurring' && selectedAgent.run_every_pay_period && (
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Pay Period</label>
                              <p className="text-sm text-gray-900 mt-1 capitalize font-medium">
                                {selectedAgent.run_every_pay_period.replace('_', ' ')}
                              </p>
                            </div>
                          )}
                          {selectedAgent.mode === 'recurring' && selectedAgent.days_after_period_end && (
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Days After Period End</label>
                              <p className="text-sm text-gray-900 mt-1 font-medium">{selectedAgent.days_after_period_end} days</p>
                            </div>
                          )}
                          {selectedAgent.mode === 'adhoc' && selectedAgent.report_period && (
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Report Period</label>
                              <p className="text-sm text-gray-900 mt-1 capitalize">
                                {selectedAgent.report_period.replace('_', ' ')}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </section>

                    {/* Email Recipients */}
                    {selectedAgent.email_recipients && selectedAgent.email_recipients.length > 0 && (
                      <section>
                        <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                          <Mail className="h-5 w-5 text-red-600 mr-2" />
                          Email Recipients
                        </h4>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="flex flex-wrap gap-2">
                            {selectedAgent.email_recipients.map((email, index) => (
                              <span 
                                key={index}
                                className="inline-flex items-center px-3 py-2 rounded text-sm font-medium bg-red-100 text-red-700 border border-red-200"
                              >
                                {email}
                              </span>
                            ))}
                          </div>
                        </div>
                      </section>
                    )}
                  </>
                ) : selectedAgent.agent_type === 'sms_agent' ? (
                  // SMS Agent View
                  <>
                    {/* Basic Information */}
                    <section>
                      <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                        <MessageCircle className="h-5 w-5 text-green-600 mr-2" />
                        SMS Agent Overview
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Agent Name</label>
                            <p className="text-sm text-gray-900 mt-1 font-medium">{selectedAgent.name || selectedAgent.agent_name}</p>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Agent Type</label>
                            <p className="text-sm text-gray-900 mt-1">SMS Agent</p>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</label>
                            <p className="text-sm text-gray-900 mt-1 font-medium capitalize">
                              {selectedAgent.is_active ? 'Active' : 'Inactive'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* Recipients */}
                    <section>
                      <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                        <Users className="h-5 w-5 text-purple-600 mr-2" />
                        Recipients
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {selectedAgent.email_type && (
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email Type</label>
                              <p className="text-sm text-gray-900 mt-1 font-medium capitalize">
                                {selectedAgent.email_type === 'single' ? 'Single Customer' : 'Bulk Email'}
                              </p>
                            </div>
                          )}
                          {selectedAgent.email_type === 'single' && selectedAgent.selected_customer && (
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Selected Customer</label>
                              <p className="text-sm text-gray-900 mt-1 font-medium">
                                {(() => {
                                  // Get customer name with robust logic (same as SMS)
                                  if (customerData && customerData.customers && Array.isArray(customerData.customers)) {
                                    // Find customer by ID
                                    const customer = customerData.customers.find(c => 
                                      String(c.id) === String(selectedAgent.selected_customer)
                                    );
                                    
                                    if (customer) {
                                      // Priority order: name field → first_name + last_name → full_name
                                      if (customer.name && customer.name.trim()) {
                                        return customer.name;
                                      }
                                      if (customer.first_name || customer.last_name) {
                                        return `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
                                      }
                                      if (customer.full_name && customer.full_name.trim()) {
                                        return customer.full_name;
                                      }
                                    }
                                  }
                                  
                                  // Fallback: show shortened ID
                                  return `Customer (${selectedAgent.selected_customer.substring(0, 8)}...)`;
                                })()}
                              </p>
                            </div>
                          )}
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Workflow Mode</label>
                            <p className="text-sm text-gray-900 mt-1 font-medium capitalize">
                              {selectedAgent.auto_post ? 'Auto Send' : 'Review Before Send'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* Scheduling & Timing */}
                    <section>
                      <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                        <Clock className="h-5 w-5 text-theme-blue mr-2" />
                        Scheduling & Timing
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {selectedAgent.post_time && (
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Send Time</label>
                              <p className="text-sm text-gray-900 mt-1 font-medium">
                                {(() => {
                                  // Convert 24-hour format to 12-hour format with AM/PM
                                  const time = selectedAgent.post_time;
                                  if (time) {
                                    const [hours, minutes] = time.split(':');
                                    const hour = parseInt(hours);
                                    const ampm = hour >= 12 ? 'PM' : 'AM';
                                    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                                    return `${hour12}:${minutes} ${ampm}`;
                                  }
                                  return time;
                                })()}
                              </p>
                            </div>
                          )}
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Next Scheduled Run</label>
                            <p className="text-sm text-gray-900 mt-1 font-medium">
                              {(() => {
                                // Holiday SMS agents - use same logic as dashboard
                                if (selectedAgent.selected_holidays && selectedAgent.selected_holidays.length > 0) {
                                  const holidayInfo = getNextScheduledHoliday(selectedAgent);
                                  return `${holidayInfo.holidayName || 'Next Holiday'}: ${holidayInfo.nextRun}`;
                                }
                                
                                // Write SMS agents - use same logic as dashboard  
                                if (selectedAgent.post_date && selectedAgent.post_time) {
                                  try {
                                    const datetimeString = `${selectedAgent.post_date}T${selectedAgent.post_time}:00`;
                                    const date = new Date(datetimeString);
                                    
                                    // Compare dates in business timezone
                                    const now = new Date();
                                    const isPastDate = date < now;
                                    
                                    const formattedDate = date.toLocaleDateString('en-US', {
                                      weekday: 'long',
                                      year: 'numeric', 
                                      month: 'long',
                                      day: 'numeric'
                                    });
                                    const formattedTime = date.toLocaleTimeString('en-US', {
                                      hour: 'numeric',
                                      minute: '2-digit',
                                      hour12: true
                                    });
                                    
                                    if (isPastDate) {
                                      return `Past date: ${formattedDate} at ${formattedTime}`;
                                    } else {
                                      return `${formattedDate} at ${formattedTime}`;
                                    }
                                  } catch (error) {
                                    return 'Invalid date/time format';
                                  }
                                }
                                
                                // Recurring SMS agents - use same logic as dashboard
                                if (selectedAgent.mode === 'recurring' || selectedAgent.mode === 'auto') {
                                  const runInfo = calculateNextRunForRecurringAgent(selectedAgent);
                                  return runInfo ? runInfo.nextRunDate : 'Not scheduled';
                                }
                                
                                return 'Not scheduled';
                              })()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* Recipients */}
                    <section>
                      <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                        <Users className="h-5 w-5 text-purple-600 mr-2" />
                        Recipients
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {selectedAgent.sms_type && (
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">SMS Type</label>
                              <p className="text-sm text-gray-900 mt-1 font-medium capitalize">
                                {selectedAgent.sms_type === 'single' ? 'Single Customer' : 'Bulk SMS'}
                              </p>
                            </div>
                          )}
                          {selectedAgent.sms_type === 'single' && selectedAgent.selected_sms_customer && (
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Selected Customer</label>
                              <p className="text-sm text-gray-900 mt-1 font-medium">
                                {(() => {
                                  // Get customer name with robust logic
                                  if (customerData && customerData.customers && Array.isArray(customerData.customers)) {
                                    // Find customer by ID
                                    const customer = customerData.customers.find(c => 
                                      String(c.id) === String(selectedAgent.selected_sms_customer)
                                    );
                                    
                                    if (customer) {
                                      // Priority order: name field → first_name + last_name → full_name
                                      if (customer.name && customer.name.trim()) {
                                        return customer.name;
                                      }
                                      if (customer.first_name || customer.last_name) {
                                        return `${customer.first_name || ''} ${customer.last_name || ''}`.trim();
                                      }
                                      if (customer.full_name && customer.full_name.trim()) {
                                        return customer.full_name;
                                      }
                                    }
                                  }
                                  
                                  // Fallback: show shortened ID
                                  return `Customer (${selectedAgent.selected_sms_customer.substring(0, 8)}...)`;
                                })()}
                              </p>
                            </div>
                          )}
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Workflow Mode</label>
                            <p className="text-sm text-gray-900 mt-1 font-medium capitalize">
                              {selectedAgent.auto_post ? 'Auto Send' : 'Review Before Send'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* SMS Content Template */}
                    {selectedAgent.sms_template && (
                      <section>
                        <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                          <MessageCircle className="h-5 w-5 text-green-600 mr-2" />
                          SMS Content
                        </h4>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedAgent.sms_template}</p>
                          </div>
                          {selectedAgent.sms_character_limit && (
                            <div className="mt-2 text-xs text-gray-600">
                              Character limit: {selectedAgent.sms_character_limit} characters
                            </div>
                          )}
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">SMS Link</label>
                            {selectedAgent.enable_sms_link ? (
                              <p className="text-sm text-gray-900 mt-1 break-all font-medium">{selectedAgent.sms_link}</p>
                            ) : (
                              <p className="text-sm text-gray-500 mt-1 font-medium">Disabled</p>
                            )}
                          </div>
                        </div>
                      </section>
                    )}

                    {/* Image Options */}
                    <section>
                      <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                        <ImageIcon className="h-5 w-5 text-orange-600 mr-2" />
                        Image Options
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {selectedAgent.image_option && (
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Image Option</label>
                              <p className="text-sm text-gray-900 mt-1 font-medium capitalize">
                                {formatImageOption(selectedAgent)}
                              </p>
                            </div>
                          )}
                          {selectedAgent.image_format && (
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Image Format</label>
                              <p className="text-sm text-gray-900 mt-1 font-medium capitalize">
                                {selectedAgent.image_format}
                              </p>
                            </div>
                          )}
                          {selectedAgent.enable_custom_image_text !== undefined && (
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Add Custom Text Banner to Image</label>
                              <p className="text-sm text-gray-900 mt-1 font-medium capitalize">
                                {selectedAgent.enable_custom_image_text ? 'Enabled' : 'Disabled'}
                              </p>
                            </div>
                          )}
                        </div>
                        
                        {/* Custom Text Banner Content - Only show if enabled */}
                        {selectedAgent.enable_custom_image_text && selectedAgent.custom_image_banner_text && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">Custom Text Banner</label>
                            <div className="bg-white border border-gray-200 rounded-lg p-3">
                              <p className="text-sm font-medium text-gray-900">{selectedAgent.custom_image_banner_text}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </section>

                    {/* Selected Holidays - Only for Holiday SMS Agents */}
                    {selectedAgent.agent_type === 'sms_agent' && selectedAgent.selected_holidays && selectedAgent.selected_holidays.length > 0 && (
                      <section>
                        <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                          <Calendar className="h-5 w-5 text-red-600 mr-2" />
                          Selected Holidays ({selectedAgent.selected_holidays?.length || 0})
                        </h4>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="max-h-60 overflow-y-auto space-y-2">
                            {selectedAgent.selected_holidays.map((holidayId) => {
                              const holiday = Array.isArray(availableHolidays) && availableHolidays.length > 0 ? 
                                availableHolidays.find(h => h.id === holidayId) : null;
                              return (
                                <div key={holidayId} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">
                                      {holiday ? holiday.name : `Holiday ID: ${holidayId.substring(0, 8)}...`}
                                    </p>
                                    {holiday && (
                                      <p className="text-xs text-gray-600 mt-1">
                                        {new Date(holiday.date).toLocaleDateString('en-US', {
                                          weekday: 'long',
                                          year: 'numeric',
                                          month: 'long',
                                          day: 'numeric'
                                        })}
                                      </p>
                                    )}
                                  </div>
                                  <div className="flex items-center">
                                    <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </section>
                    )}

 

 
                  </>
                ) : selectedAgent.agent_type === 'email' ? (
                  // Email Agent View
                  <>
                    {/* Basic Information */}
                    <section>
                      <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                        <Mail className="h-5 w-5 text-theme-blue mr-2" />
                        Email Agent Overview
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Agent Name</label>
                            <p className="text-sm text-gray-900 mt-1 font-medium">{selectedAgent.name || selectedAgent.agent_name}</p>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Topic</label>
                            <p className="text-sm text-gray-900 mt-1">
                              {selectedAgent.selected_holidays && selectedAgent.selected_holidays.length > 0 ? 
                                'Send Email for Selected Holidays' : 
                                (selectedAgent.topic || selectedAgent.email_subject || 'Email Communication')
                              }
                            </p>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email Recipients</label>
                            <p className="text-sm text-gray-900 mt-1">
                              {selectedAgent.email_type === 'single' ? 
                                (() => {
                                  // Use same logic as dashboard for Email Recipients
                                  const customerId = selectedAgent.selected_email_customer || selectedAgent.selected_customer;
                                  if (customerId && customerData && customerData.customers && Array.isArray(customerData.customers)) {
                                    const customer = customerData.customers.find(c => 
                                      String(c.id) === String(customerId)
                                    );
                                    if (customer) {
                                      // Priority order: name field → first_name + last_name → full_name
                                      if (customer.name && customer.name.trim()) {
                                        return `Single Customer: ${customer.name}`;
                                      }
                                      if (customer.first_name || customer.last_name) {
                                        return `Single Customer: ${customer.first_name || ''} ${customer.last_name || ''}`.trim();
                                      }
                                      if (customer.full_name && customer.full_name.trim()) {
                                        return `Single Customer: ${customer.full_name}`;
                                      }
                                    }
                                  }
                                  // Fallback
                                  return 'Single Customer';
                                })() : 
                                'All customers in database'
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* Scheduling & Timing */}
                    <section>
                      <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                        <Clock className="h-5 w-5 text-theme-blue mr-2" />
                        Scheduling & Timing
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Next Scheduled Run</label>
                            <p className="text-sm text-gray-900 mt-1">
                              {(() => {
                                // Holiday Email agents - use same logic as dashboard
                                if (selectedAgent.selected_holidays && selectedAgent.selected_holidays.length > 0) {
                                  const holidayInfo = getNextScheduledHoliday(selectedAgent);
                                  return `${holidayInfo.holidayName || 'Next Holiday'}: ${holidayInfo.nextRun}`;
                                }
                                
                                // Write Email agents - use same logic as dashboard  
                                if (selectedAgent.post_date && selectedAgent.post_time) {
                                  try {
                                    const datetimeString = `${selectedAgent.post_date}T${selectedAgent.post_time}:00`;
                                    const date = new Date(datetimeString);
                                    
                                    const formattedDate = date.toLocaleDateString('en-US', {
                                      weekday: 'long',
                                      year: 'numeric', 
                                      month: 'long',
                                      day: 'numeric'
                                    });
                                    const formattedTime = date.toLocaleTimeString('en-US', {
                                      hour: 'numeric',
                                      minute: '2-digit',
                                      hour12: true
                                    });
                                    
                                    return `${formattedDate} at ${formattedTime}`;
                                  } catch (error) {
                                    return 'Invalid date/time format';
                                  }
                                }
                                
                                // Recurring Email agents
                                if (selectedAgent.mode === 'recurring') {
                                  const runInfo = calculateNextRunForRecurringAgent(selectedAgent);
                                  return runInfo ? runInfo.nextRunDate : 'Not scheduled';
                                }
                                
                                return 'Not scheduled';
                              })()}
                            </p>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Content Review Workflow</label>
                            <p className="text-sm text-gray-900 mt-1">{getWorkflowMode(selectedAgent)}</p>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* Email Content Template */}
                    <section>
                      <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                        <FileText className="h-5 w-5 text-purple-600 mr-2" />
                        Email Content
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        {selectedAgent.email_content_template || selectedAgent.email_content ? (
                          <div className="bg-white border border-gray-200 rounded-lg p-4">
                            <p className="text-sm text-gray-900 whitespace-pre-wrap">
                              {selectedAgent.email_content_template || selectedAgent.email_content}
                            </p>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">No email content specified</p>
                        )}
                      </div>
                    </section>

                    {/* Image Options */}
                    <section>
                      <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                        <ImageIcon className="h-5 w-5 text-indigo-600 mr-2" />
                        Image Options
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Image Option</label>
                            <p className="text-sm text-gray-900 mt-1 capitalize">{formatImageOption(selectedAgent)}</p>
                          </div>
                          {(selectedAgent.image_format || selectedAgent.platform_image_formats?.email) && (
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Image Format Type</label>
                              <div className="mt-1">
                                <p className="text-sm text-gray-900 font-medium capitalize">
                                  {selectedAgent.image_format || selectedAgent.platform_image_formats?.email}
                                </p>
                                {(() => {
                                  const format = selectedAgent.image_format || selectedAgent.platform_image_formats?.email;
                                  const dimensions = getImageDimensions('email', format);
                                  return dimensions ? (
                                    <p className="text-xs text-gray-600 mt-1">
                                      {dimensions}
                                    </p>
                                  ) : null;
                                })()}
                              </div>
                            </div>
                          )}
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Add Custom Text Banner to Image</label>
                            <p className="text-sm text-gray-900 mt-1 font-medium">
                              {selectedAgent.enable_custom_image_text ? 'Enabled' : 'Disabled'}
                            </p>
                          </div>
                        </div>
                        
                        {/* Custom Text Banner Content - Only show if enabled */}
                        {selectedAgent.enable_custom_image_text && selectedAgent.custom_image_banner_text && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">Custom Text Banner</label>
                            <div className="bg-white border border-gray-200 rounded-lg p-3">
                              <p className="text-sm font-medium text-gray-900">{selectedAgent.custom_image_banner_text}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </section>

                    {/* Selected Holidays - Only for Holiday Email Agents */}
                    {selectedAgent.selected_holidays && selectedAgent.selected_holidays.length > 0 && (
                      <section>
                        <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                          <Calendar className="h-5 w-5 text-red-600 mr-2" />
                          Selected Holidays ({selectedAgent.selected_holidays?.length || 0})
                        </h4>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="max-h-60 overflow-y-auto space-y-2">
                            {selectedAgent.selected_holidays.map((holidayId) => {
                              const holiday = Array.isArray(availableHolidays) && availableHolidays.length > 0 ? 
                                availableHolidays.find(h => h.id === holidayId) : null;
                              return (
                                <div key={holidayId} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">
                                      {holiday ? holiday.name : `Holiday ID: ${holidayId.substring(0, 8)}...`}
                                    </p>
                                    {holiday && (
                                      <p className="text-xs text-gray-600 mt-1">
                                        {new Date(holiday.date).toLocaleDateString('en-US', {
                                          weekday: 'long',
                                          year: 'numeric',
                                          month: 'long',
                                          day: 'numeric'
                                        })}
                                      </p>
                                    )}
                                  </div>
                                  <div className="flex items-center">
                                    <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </section>
                    )}

                  </>
                ) : selectedAgent.agent_type && selectedAgent.agent_type.includes('time') ? (
                  // Fallback for timesheet agents with different naming
                  <section>
                    <h4 className="text-base font-medium text-gray-900 mb-2">
                      Timesheet Agent (Fallback View)
                    </h4>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-sm text-red-800">
                        Timesheet agent detected but agent_type is: "{selectedAgent.agent_type}"
                      </p>
                      <pre className="text-xs mt-2 text-red-700">
                        {JSON.stringify(selectedAgent, null, 2)}
                      </pre>
                    </div>
                  </section>
                ) : selectedAgent.agent_type === 'marketing_agent' ? (
                  // Marketing Agent View
                  <>
                    {/* Marketing Agent Overview */}
                    <section>
                      <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                        <MessageCircle className="h-5 w-5 text-purple-600 mr-2" />
                        Marketing Agent Overview
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Agent Name</label>
                            <p className="text-sm text-gray-900 mt-1 font-medium">{selectedAgent.name || selectedAgent.agent_name}</p>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Agent Type</label>
                            <p className="text-sm text-gray-900 mt-1">Marketing Campaign</p>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Selected Channels</label>
                            <p className="text-sm text-gray-900 mt-1">
                              {selectedAgent.marketing_channels && selectedAgent.marketing_channels.length > 0 ? (
                                selectedAgent.marketing_channels.map(channel => 
                                  channel === 'social_media' ? 'Social Media' : 
                                  channel === 'sms' ? 'SMS' :
                                  channel.charAt(0).toUpperCase() + channel.slice(1)
                                ).join(', ')
                              ) : 'None selected'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* Content Section */}
                    <section>
                      <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                        <FileText className="h-5 w-5 text-purple-600 mr-2" />
                        Content
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Topic</label>
                            <p className="text-sm text-gray-900 mt-1">{getMarketingContentTypeDisplay(selectedAgent)}</p>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Content Workflow Mode</label>
                            <p className="text-sm text-gray-900 mt-1">
                              In Review → Ready to Publish → Publish
                            </p>
                          </div>
                        </div>
                        
                        {/* Custom Campaign Content or Holiday List */}
                        {selectedAgent.marketing_content_type === 'custom_campaign' && selectedAgent.marketing_custom_campaign && (
                          <div className="mt-4">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Campaign Content</label>
                            <div className="mt-2 p-3 bg-white rounded-md border border-gray-200 max-h-32 overflow-y-auto">
                              <p className="text-sm text-gray-900 whitespace-pre-wrap">
                                {selectedAgent.marketing_custom_campaign}
                              </p>
                            </div>
                          </div>
                        )}
                        
                        {selectedAgent.marketing_content_type === 'holidays' && selectedAgent.selected_holidays && selectedAgent.selected_holidays.length > 0 && (
                          <div className="mt-4">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Selected Holidays ({selectedAgent.selected_holidays?.length || 0})</label>
                            <div className="mt-2 p-3 bg-white rounded-md border border-gray-200 max-h-32 overflow-y-auto space-y-2">
                              {selectedAgent.selected_holidays.map((holidayId) => {
                                const holiday = Array.isArray(availableHolidays) && availableHolidays.length > 0 ? 
                                  availableHolidays.find(h => h.id === holidayId) : null;
                                return (
                                  <div key={holidayId} className="flex items-center justify-between p-2 bg-gray-50 rounded border border-gray-100">
                                    <div className="flex-1">
                                      <p className="text-sm font-medium text-gray-900">
                                        {holiday ? holiday.name : `Holiday ID: ${holidayId.substring(0, 8)}...`}
                                      </p>
                                      {holiday && (
                                        <p className="text-xs text-gray-600 mt-1">
                                          {new Date(holiday.date).toLocaleDateString('en-US', {
                                            weekday: 'long',
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                          })}
                                        </p>
                                      )}
                                    </div>
                                    <div className="flex items-center">
                                      <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </section>

                    {/* Scheduling & Timing */}
                    <section>
                      <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                        <Clock className="h-5 w-5 text-theme-blue mr-2" />
                        Scheduling & Timing
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {selectedAgent.post_date && (
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Scheduled Date</label>
                              <p className="text-sm text-gray-900 mt-1 font-medium">
                                {new Date(selectedAgent.post_date).toLocaleDateString('en-US', {
                                  weekday: 'short',
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </p>
                            </div>
                          )}
                          {selectedAgent.post_time && (
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Post Time</label>
                              <p className="text-sm text-gray-900 mt-1 font-medium">
                                {(() => {
                                  const time = selectedAgent.post_time;
                                  if (time) {
                                    const [hours, minutes] = time.split(':');
                                    const hour = parseInt(hours);
                                    const ampm = hour >= 12 ? 'PM' : 'AM';
                                    const hour12 = hour % 12 || 12;
                                    return `${hour12}:${minutes} ${ampm}`;
                                  }
                                  return time;
                                })()}
                              </p>
                            </div>
                          )}
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Next Scheduled Run</label>
                            <p className="text-sm text-gray-900 mt-1 font-medium">
                              {(() => {
                                if (selectedAgent.post_date && selectedAgent.post_time) {
                                  try {
                                    const datetimeString = `${selectedAgent.post_date}T${selectedAgent.post_time}:00`;
                                    const date = new Date(datetimeString);
                                    const now = new Date();
                                    const isPastDate = date < now;
                                    
                                    const formattedDate = date.toLocaleDateString('en-US', {
                                      weekday: 'long',
                                      year: 'numeric', 
                                      month: 'long',
                                      day: 'numeric'
                                    });
                                    const formattedTime = date.toLocaleTimeString('en-US', {
                                      hour: 'numeric',
                                      minute: '2-digit',
                                      hour12: true
                                    });
                                    
                                    if (isPastDate) {
                                      return `Past date: ${formattedDate} at ${formattedTime}`;
                                    } else {
                                      return `${formattedDate} at ${formattedTime}`;
                                    }
                                  } catch (error) {
                                    return 'Invalid date/time format';
                                  }
                                }
                                return 'Configured for Manual Run';
                              })()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* Social Media Section */}
                    {selectedAgent.marketing_channels && selectedAgent.marketing_channels.includes('social_media') && (
                      <section>
                        <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                          <Share2 className="h-5 w-5 text-purple-600 mr-2" />
                          Social Media
                        </h4>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Social Media Platforms</label>
                              <div className="mt-1">
                                {selectedAgent.marketing_social_platforms && Object.keys(selectedAgent.marketing_social_platforms).length > 0 ? (
                                  <div className="flex flex-wrap gap-2">
                                    {Object.entries(selectedAgent.marketing_social_platforms)
                                      .filter(([platform, enabled]) => enabled)
                                      .map(([platform]) => (
                                        <span key={platform} className="text-xs text-gray-700">
                                          {platform === 'twitter' ? 'X (Twitter)' : platform.charAt(0).toUpperCase() + platform.slice(1)}
                                        </span>
                                      ))}
                                    {Object.values(selectedAgent.marketing_social_platforms).every(enabled => !enabled) && (
                                      <span className="text-xs text-gray-600">No platforms selected</span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-600">No platforms configured</span>
                                )}
                              </div>
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Image Option</label>
                              <p className="text-sm text-gray-900 mt-1">
                                {selectedAgent.image_option === 'none' || selectedAgent.image_option === 'text_only' ? 'Text Only' : 'Enabled'}
                              </p>
                            </div>
                          </div>
                          
                          {/* Image Format for Each Platform */}
                          {selectedAgent.image_option && selectedAgent.image_option !== 'none' && selectedAgent.image_option !== 'text_only' && (
                            <div className="mt-4">
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Select Image Format for Each Platform</label>
                              <div className="mt-2 p-3 bg-white rounded-md border border-gray-200">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                  {selectedAgent.marketing_social_platforms && Object.entries(selectedAgent.marketing_social_platforms)
                                    .filter(([platform, enabled]) => enabled)
                                    .map(([platform]) => (
                                      <div key={platform}>
                                        <p className="text-sm font-medium text-gray-900">
                                          {platform === 'twitter' ? 'X (Twitter)' : platform.charAt(0).toUpperCase() + platform.slice(1)}
                                        </p>
                                        <p className="text-xs text-gray-600">
                                          {(() => {
                                            const format = selectedAgent.platform_image_formats?.[platform] || 'square';
                                            const dimensions = getImageDimensions(platform, format);
                                            return `${format.charAt(0).toUpperCase() + format.slice(1)}${dimensions ? ` (${dimensions})` : ''}`;
                                          })()}
                                        </p>
                                      </div>
                                    ))}
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Add Custom Text Banner to Image - Moved below Select Image Format */}
                          {selectedAgent.enable_custom_image_text && selectedAgent.custom_image_banner_text && (
                            <div className="mt-4 pt-4 border-t border-gray-200">
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">Add Custom Text Banner to Image</label>
                              <div className="bg-white border border-gray-200 rounded-lg p-3">
                                <p className="text-sm font-medium text-gray-900">{selectedAgent.custom_image_banner_text}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </section>
                    )}

                    {/* Email Section */}
                    {selectedAgent.marketing_channels && selectedAgent.marketing_channels.includes('email') && (
                      <section>
                        <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                          <Mail className="h-5 w-5 text-purple-600 mr-2" />
                          Email
                        </h4>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Include Image</label>
                              <p className="text-sm text-gray-900 mt-1">
                                {selectedAgent.marketing_email_include_image ? 'Yes' : 'No'}
                              </p>
                            </div>
                            {selectedAgent.marketing_email_include_image && selectedAgent.platform_image_formats?.email && (
                              <div>
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email Image Format</label>
                                <p className="text-sm text-gray-900 mt-1">
                                  {(() => {
                                    const format = selectedAgent.platform_image_formats.email;
                                    const dimensions = getImageDimensions('email', format);
                                    return `${format.charAt(0).toUpperCase() + format.slice(1)}${dimensions ? ` (${dimensions})` : ''}`;
                                  })()}
                                </p>
                              </div>
                            )}
                          </div>
                          {selectedAgent.email_content_template && (
                            <div className="mt-4">
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email Content</label>
                              <div className="mt-2 p-3 bg-white rounded-md border border-gray-200 max-h-32 overflow-y-auto">
                                <p className="text-sm text-gray-900 whitespace-pre-wrap">
                                  {selectedAgent.email_content_template}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </section>
                    )}

                    {/* SMS Section */}
                    {selectedAgent.marketing_channels && selectedAgent.marketing_channels.includes('sms') && (
                      <section>
                        <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                          <MessageSquare className="h-5 w-5 text-purple-600 mr-2" />
                          SMS
                        </h4>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Include Image</label>
                              <p className="text-sm text-gray-900 mt-1">
                                {selectedAgent.marketing_sms_include_image ? 'Yes' : 'No'}
                              </p>
                            </div>
                            {selectedAgent.marketing_sms_include_image && selectedAgent.platform_image_formats?.sms && (
                              <div>
                                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">SMS Image Format</label>
                                <p className="text-sm text-gray-900 mt-1">
                                  {(() => {
                                    const format = selectedAgent.platform_image_formats.sms;
                                    const dimensions = getImageDimensions('sms', format);
                                    return `${format.charAt(0).toUpperCase() + format.slice(1)}${dimensions ? ` (${dimensions})` : ''}`;
                                  })()}
                                </p>
                              </div>
                            )}
                          </div>
                          {selectedAgent.sms_template && (
                            <div className="mt-4">
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">SMS Content</label>
                              <div className="mt-2 p-3 bg-white rounded-md border border-gray-200 max-h-32 overflow-y-auto">
                                <p className="text-sm text-gray-900 whitespace-pre-wrap">
                                  {selectedAgent.sms_template}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </section>
                    )}
                  </>
                ) : selectedAgent.agent_type === 'paystub_agent' ? (
                  // Paystub Agent View
                  <>
                    {/* Paystub Agent Overview */}
                    <section>
                      <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                        <FileText className="h-5 w-5 text-orange-600 mr-2" />
                        Paystub Agent Overview
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Agent Name</label>
                            <p className="text-sm text-gray-900 mt-1 font-medium">{selectedAgent.name || selectedAgent.agent_name}</p>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Agent Type</label>
                            <p className="text-sm text-gray-900 mt-1">Paystub</p>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</label>
                            <p className="text-sm text-gray-900 mt-1 font-medium capitalize">
                              {selectedAgent.is_active ? 'Active' : 'Inactive'}
                            </p>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Mode</label>
                            <p className="text-sm text-gray-900 mt-1 font-medium">
                              {selectedAgent.mode === 'paystub_adhoc' || selectedAgent.mode === 'adhoc' ? 'Adhoc' : 'Recurring'}
                            </p>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Paystub Format</label>
                            <p className="text-sm text-gray-900 mt-1">
                              {selectedAgent.paystub_format === 'pdf' ? 'PDF Document' : selectedAgent.paystub_format === 'email' ? 'Email Format' : 'PDF Document'}
                            </p>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Workflow Mode</label>
                            <p className="text-sm text-gray-900 mt-1">
                              {getWorkflowMode(selectedAgent)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* Scheduling & Timing */}
                    {(selectedAgent.mode === 'paystub_recurring' || selectedAgent.mode === 'recurring') && (
                      <section>
                        <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                          <Clock className="h-5 w-5 text-theme-blue mr-2" />
                          Scheduling & Timing
                        </h4>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Days After Timesheet Published</label>
                              <p className="text-sm text-gray-900 mt-1 font-medium">
                                {selectedAgent.days_after_timesheet_published ? `${selectedAgent.days_after_timesheet_published} day${selectedAgent.days_after_timesheet_published !== 1 ? 's' : ''}` : '1 day'}
                              </p>
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Next Execution</label>
                              <p className="text-sm text-gray-900 mt-1 font-medium">
                                {(() => {
                                  const periodInfo = calculatePayPeriodAndRunDate(selectedAgent);
                                  return periodInfo ? periodInfo.nextRunDate : 'Calculating...';
                                })()}
                              </p>
                            </div>
                          </div>
                        </div>
                      </section>
                    )}

                    {/* Employees Section */}
                    <section>
                      <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                        <Users className="h-5 w-5 text-purple-600 mr-2" />
                        Employees
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex flex-wrap gap-2">
                          {selectedAgent.selected_employees && selectedAgent.selected_employees.length > 0 ? (
                            selectedAgent.selected_employees.map((employeeId, index) => (
                              <span 
                                key={index}
                                className="inline-flex items-center px-3 py-2 rounded text-sm font-medium bg-purple-100 text-purple-700 border border-purple-200"
                              >
                                {getEmployeeName(employeeId)}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-gray-500">All employees selected</span>
                          )}
                        </div>
                      </div>
                    </section>

                    {/* Adhoc Mode: Selected Timesheet Details */}
                    {(selectedAgent.mode === 'paystub_adhoc' || selectedAgent.mode === 'adhoc') && selectedAgent.selected_timesheet_id && (
                      <section>
                        <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                          <Calendar className="h-5 w-5 text-theme-blue mr-2" />
                          Selected Timesheet
                        </h4>
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Timesheet</label>
                              <p className="text-sm text-gray-900 mt-1 font-medium">
                                {formatTimesheetDisplay(selectedAgent.selected_timesheet_id, selectedAgent.timesheet_data)}
                              </p>
                            </div>
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Pay Period</label>
                              <p className="text-sm text-gray-900 mt-1 font-medium">
                                {getTimesheetPayPeriod(selectedAgent)}
                              </p>
                            </div>
                          </div>
                        </div>
                      </section>
                    )}
                  </>
                ) : (
                  // Social Media Agent View
                  <>
                    {/* Social Media Agent Overview */}
                    <section>
                      <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                        <Share2 className="h-5 w-5 text-theme-blue mr-2" />
                        Social Media Agent Overview
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Agent Name</label>
                            <p className="text-sm text-gray-900 mt-1 font-medium">{selectedAgent.name || selectedAgent.agent_name}</p>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Agent Type</label>
                            <p className="text-sm text-gray-900 mt-1">Social Media</p>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</label>
                            <p className="text-sm text-gray-900 mt-1 font-medium capitalize">
                              {selectedAgent.is_active ? 'Active' : 'Inactive'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* Scheduling & Timing */}
                    <section>
                      <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                        <Clock className="h-5 w-5 text-theme-blue mr-2" />
                        Scheduling & Timing
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {selectedAgent.mode === 'adhoc' && selectedAgent.post_date && (
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Post Date</label>
                              <p className="text-sm text-gray-900 mt-1 font-medium">
                                {new Date(selectedAgent.post_date).toLocaleDateString('en-US', {
                                  weekday: 'short',
                                  month: 'short',
                                  day: 'numeric',
                                  year: 'numeric'
                                })}
                              </p>
                            </div>
                          )}
                          {selectedAgent.post_time && (
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Post Time</label>
                              <p className="text-sm text-gray-900 mt-1 font-medium">
                                {(() => {
                                  const time = selectedAgent.post_time;
                                  if (time) {
                                    const [hours, minutes] = time.split(':');
                                    const hour = parseInt(hours);
                                    const ampm = hour >= 12 ? 'PM' : 'AM';
                                    const hour12 = hour % 12 || 12;
                                    return `${hour12}:${minutes} ${ampm}`;
                                  }
                                  return time;
                                })()}
                              </p>
                            </div>
                          )}
                          {(selectedAgent.mode === 'auto' || selectedAgent.mode === 'recurring') && selectedAgent.frequency && (
                            <div>
                              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Frequency</label>
                              <p className="text-sm text-gray-900 mt-1 font-medium">
                                Every {selectedAgent.frequency} hours
                              </p>
                            </div>
                          )}
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Next Scheduled Run</label>
                            <p className="text-sm text-gray-900 mt-1 font-medium">
                              {(() => {
                                // Write mode agents with scheduled post date
                                if (selectedAgent.mode === 'write' && selectedAgent.post_date && selectedAgent.post_time) {
                                  try {
                                    const datetimeString = `${selectedAgent.post_date}T${selectedAgent.post_time}:00`;
                                    const date = new Date(datetimeString);
                                    
                                    // Compare dates
                                    const now = new Date();
                                    const isPastDate = date < now;
                                    
                                    const formattedDate = date.toLocaleDateString('en-US', {
                                      weekday: 'long',
                                      year: 'numeric', 
                                      month: 'long',
                                      day: 'numeric'
                                    });
                                    const formattedTime = date.toLocaleTimeString('en-US', {
                                      hour: 'numeric',
                                      minute: '2-digit',
                                      hour12: true
                                    });
                                    
                                    if (isPastDate) {
                                      return `Past date: ${formattedDate} at ${formattedTime}`;
                                    } else {
                                      return `${formattedDate} at ${formattedTime}`;
                                    }
                                  } catch (error) {
                                    return 'Invalid date/time format';
                                  }
                                }
                                
                                // Adhoc mode agents with scheduled post date
                                if (selectedAgent.mode === 'adhoc' && selectedAgent.post_date && selectedAgent.post_time) {
                                  try {
                                    const datetimeString = `${selectedAgent.post_date}T${selectedAgent.post_time}:00`;
                                    const date = new Date(datetimeString);
                                    
                                    // Compare dates
                                    const now = new Date();
                                    const isPastDate = date < now;
                                    
                                    const formattedDate = date.toLocaleDateString('en-US', {
                                      weekday: 'long',
                                      year: 'numeric', 
                                      month: 'long',
                                      day: 'numeric'
                                    });
                                    const formattedTime = date.toLocaleTimeString('en-US', {
                                      hour: 'numeric',
                                      minute: '2-digit',
                                      hour12: true
                                    });
                                    
                                    if (isPastDate) {
                                      return `Past date: ${formattedDate} at ${formattedTime}`;
                                    } else {
                                      return `${formattedDate} at ${formattedTime}`;
                                    }
                                  } catch (error) {
                                    return 'Invalid date/time format';
                                  }
                                }
                                
                                // Recurring agents - use same logic as dashboard
                                if (selectedAgent.mode === 'recurring' || selectedAgent.mode === 'auto') {
                                  const runInfo = calculateNextRunForRecurringAgent(selectedAgent);
                                  return runInfo ? runInfo.nextRunDate : 'Not scheduled';
                                }
                                
                                return 'Not scheduled';
                              })()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* Content Section */}
                    <section>
                      <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                        <FileText className="h-5 w-5 text-purple-600 mr-2" />
                        Content
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Topic</label>
                            <p className="text-sm text-gray-900 mt-1">{selectedAgent.topic || 'Not specified'}</p>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Word Count</label>
                            <p className="text-sm text-gray-900 mt-1">{selectedAgent.word_count || 'Not specified'}</p>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">ChatGPT Formatting</label>
                            <p className="text-sm text-gray-900 mt-1">
                              {selectedAgent.mode === 'write' 
                                ? (selectedAgent.use_chatgpt_formatting !== undefined ? selectedAgent.use_chatgpt_formatting : true) ? 'Enabled' : 'Disabled'
                                : 'Enabled'
                              }
                            </p>
                          </div>
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Content Workflow Mode</label>
                            <p className="text-sm text-gray-900 mt-1">
                              {getWorkflowMode(selectedAgent)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </section>

                    {/* Social Media Section */}
                    <section>
                      <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                        <Share2 className="h-5 w-5 text-purple-600 mr-2" />
                        Social Media
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Social Media Platforms</label>
                            <div className="mt-1">
                              {selectedAgent.social_platforms && Object.keys(selectedAgent.social_platforms).length > 0 ? (
                                <div className="flex flex-wrap gap-2">
                                  {Object.entries(selectedAgent.social_platforms)
                                    .filter(([platform, enabled]) => enabled)
                                    .map(([platform]) => (
                                      <span key={platform} className="text-sm text-gray-700">
                                        {platform === 'twitter' ? 'X (Twitter)' : platform.charAt(0).toUpperCase() + platform.slice(1)}
                                      </span>
                                    ))}
                                  {Object.values(selectedAgent.social_platforms).every(enabled => !enabled) && (
                                    <span className="text-sm text-gray-600">No platforms selected</span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-sm text-gray-600">No platforms configured</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Add Link - Moved to Social Media section */}
                        {selectedAgent.enable_social_link && selectedAgent.social_link && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">Add Link</label>
                            <div className="bg-white border border-gray-200 rounded-lg p-3">
                              <p className="text-sm font-medium text-gray-900 break-all">{selectedAgent.social_link}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </section>

                    {/* Image Options Section - NEW */}
                    <section>
                      <h4 className="text-base font-medium text-gray-900 mb-2 flex items-center">
                        <ImageIcon className="h-5 w-5 text-orange-600 mr-2" />
                        Image Options
                      </h4>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div>
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Image Option</label>
                            <p className="text-sm text-gray-900 mt-1">
                              {selectedAgent.image_option === 'none' || selectedAgent.image_option === 'text_only' ? 'Text Only' : 'Enabled'}
                            </p>
                          </div>
                        </div>
                        
                        {/* Image Format for Each Platform */}
                        {selectedAgent.image_option && selectedAgent.image_option !== 'none' && selectedAgent.image_option !== 'text_only' && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Select Image Format for Each Platform</label>
                            <div className="mt-2 p-3 bg-white rounded-md border border-gray-200">
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {selectedAgent.social_platforms && Object.entries(selectedAgent.social_platforms)
                                  .filter(([platform, enabled]) => enabled)
                                  .map(([platform]) => (
                                    <div key={platform}>
                                      <p className="text-sm font-medium text-gray-900">
                                        {platform === 'twitter' ? 'X (Twitter)' : platform.charAt(0).toUpperCase() + platform.slice(1)}
                                      </p>
                                      <p className="text-xs text-gray-600">
                                        {(() => {
                                          const format = selectedAgent.platform_image_formats?.[platform] || 'square';
                                          const dimensionsString = getImageDimensions(platform, format);
                                          return dimensionsString ? `${format.charAt(0).toUpperCase() + format.slice(1)} (${dimensionsString})` : format;
                                        })()}
                                      </p>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Add Custom Text Banner to Image */}
                        {selectedAgent.enable_custom_image_text && selectedAgent.custom_image_banner_text && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 block">Add Custom Text Banner to Image</label>
                            <div className="bg-white border border-gray-200 rounded-lg p-3">
                              <p className="text-sm font-medium text-gray-900">{selectedAgent.custom_image_banner_text}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </section>
                  </>
                )}
              </div>

            </div>

            {/* Sticky Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <div className="flex justify-end">
                <button
                  onClick={() => setSelectedAgent(null)}
                  className="px-6 py-2 text-white rounded-lg transition-colors font-medium"
                  style={{ backgroundColor: 'var(--theme-primary)' }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = 'var(--theme-primary-hover)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'var(--theme-primary)';
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && agentToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 rounded-t-lg" style={{ backgroundColor: 'var(--theme-primary)' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 rounded-lg bg-white bg-opacity-20">
                    <Trash2 className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white">
                      Delete AI Agent
                    </h3>
                    <p className="text-white text-opacity-80 text-sm mt-1">
                      This action cannot be undone
                    </p>
                  </div>
                </div>
                <button
                  onClick={closeDeleteModal}
                  className="text-white hover:text-white hover:text-opacity-80 transition-colors p-1 rounded-full hover:bg-white hover:bg-opacity-20"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="px-6 py-6">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  Are you sure you want to delete this agent?
                </h3>
                <p className="text-sm text-gray-500 mb-4">
                  You are about to permanently delete <strong>"{agentToDelete.name || agentToDelete.agent_name || 'Unnamed Agent'}"</strong>. 
                  This will remove all associated data and cannot be undone.
                </p>
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-400 mt-0.5 mr-2" />
                    <div className="text-sm text-red-700">
                      <p className="font-medium">This action will:</p>
                      <ul className="mt-1 list-disc list-inside">
                        <li>Permanently delete the agent</li>
                        <li>Remove all agent configurations</li>
                        <li>Stop any scheduled posts</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <div className="flex justify-end space-x-3">
                <button
                  onClick={closeDeleteModal}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteAgent}
                  disabled={processingAgent === agentToDelete.id}
                  className="px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center"
                  style={{ 
                    backgroundColor: processingAgent === agentToDelete.id ? '#94a3b8' : 'var(--theme-primary)'
                  }}
                  onMouseEnter={(e) => {
                    if (processingAgent !== agentToDelete.id) {
                      e.target.style.backgroundColor = 'var(--theme-primary-hover)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (processingAgent !== agentToDelete.id) {
                      e.target.style.backgroundColor = 'var(--theme-primary)';
                    }
                  }}
                >
                  {processingAgent === agentToDelete.id ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Agent
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Run Agent Modal */}
      {showRunModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center">
                <Bot className="h-6 w-6 text-theme-blue mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Running Agent: {runningAgent?.agent_name || runningAgent?.name || 'AI Agent'}
                </h3>
              </div>
            </div>
            <div className="px-6 py-6">
              <div className="flex items-center space-x-4">
                {runStatus === 'running' && (
                  <RefreshCw className="h-8 w-8 text-theme-blue animate-spin" />
                )}
                {runStatus === 'completed' && (
                  <CheckCircle className="h-8 w-8 text-green-600" />
                )}
                {runStatus === 'warning' && (
                  <AlertCircle className="h-8 w-8 text-yellow-600" />
                )}
                {runStatus === 'error' && (
                  <XCircle className="h-8 w-8 text-red-600" />
                )}
                <div className="flex-1">
                  <p className="text-gray-900 font-medium">
                    {runStatus === 'running' && 'Generating Content...'}
                    {runStatus === 'completed' && 'Content Generated!'}
                    {runStatus === 'warning' && 'Request Timed Out'}
                    {runStatus === 'error' && 'Generation Failed'}
                  </p>
                  <p className="text-sm text-gray-600 mt-1">
                    {runMessage}
                  </p>
                </div>
              </div>
            </div>
            {(runStatus === 'error' || runStatus === 'warning') && (
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
                <div className="flex justify-end space-x-3">
                  {runStatus === 'warning' && (
                    <button
                      onClick={() => {
                        window.location.href = '/ai-in-review';
                      }}
                      className="px-4 py-2 text-white bg-theme-blue rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                      Check In Review
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setShowRunModal(false);
                      setRunningAgent(null);
                      setRunStatus('');
                      setRunMessage('');
                    }}
                    className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* AI Connection Error Modal - Clean & Compact */}
      {showAIErrorModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                    <AlertCircle className="h-6 w-6 text-red-600" />
                  </div>
                </div>
                <div className="ml-4 flex-1">
                  <h3 className="text-base font-semibold text-gray-900 mb-2">
                    ChatGPT Connection Failed
                  </h3>
                  <p className="text-sm text-gray-600 mb-1">
                    Unable to connect to the AI service. Please try again in a few moments.
                  </p>
                  <p className="text-sm text-gray-500">
                    If the issue persists, contact support.
                  </p>
                </div>
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowAIErrorModal(false)}
                  className="px-4 py-2 bg-gray-900 text-white text-sm font-medium rounded-md hover:bg-gray-800 transition-colors"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Timesheet Warning Modal */}
      {showDuplicateTimesheetModal && duplicateTimesheetInfo && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center">
                <AlertCircle className="h-6 w-6 text-amber-600 mr-3" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Replace Existing Timesheet?
                </h3>
              </div>
            </div>
            <div className="px-6 py-6">
              <div className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div>
                      <h4 className="text-sm font-medium text-amber-800 mb-1">
                        Timesheet Already Exists
                      </h4>
                      <p className="text-sm text-amber-700">
                        A timesheet for this pay period already exists and is currently <strong className="capitalize">{duplicateTimesheetInfo.status.replace('_', ' ')}</strong>.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <h5 className="text-sm font-medium text-gray-900 mb-2">Existing Timesheet Details:</h5>
                    <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Pay Period:</span>
                        <span className="text-sm font-medium text-gray-900">{duplicateTimesheetInfo.period}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Current Status:</span>
                        <span className={`text-sm font-medium capitalize ${
                          duplicateTimesheetInfo.status === 'published' ? 'text-green-700' :
                          duplicateTimesheetInfo.status === 'ready_to_publish' ? 'text-blue-700' :
                          'text-amber-700'
                        }`}>
                          {duplicateTimesheetInfo.status.replace('_', ' ')}
                        </span>
                      </div>
                      {duplicateTimesheetInfo.existingTimesheet.created_at && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Created:</span>
                          <span className="text-sm font-medium text-gray-900">
                            {new Date(duplicateTimesheetInfo.existingTimesheet.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <p className="text-sm text-red-700">
                          <strong>If you continue:</strong>
                        </p>
                        <ul className="text-sm text-red-700 mt-1 list-disc list-inside">
                          <li>The existing timesheet will be <strong>permanently deleted</strong></li>
                          <li>A new timesheet will be generated with current data</li>
                          <li>The new timesheet will start in "In Review" status</li>
                          <li>This action cannot be undone</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => handleDuplicateTimesheetChoice(false)}
                  className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDuplicateTimesheetChoice(true)}
                  className="px-4 py-2 text-white bg-red-600 border border-red-600 rounded-lg hover:bg-red-700 transition-colors font-medium"
                >
                  Delete & Replace
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAgentsDashboard;